import FeatureAction from './FeatureAction';
import LockedFeaturePanel from './LockedFeaturePanel';
import React, { useState, useEffect, useMemo, useRef, useCallback, useLayoutEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Plus,
  Trash2,
  Upload,
  Cloud,
  CheckCircle,
  Video as VideoIcon,
  Database,
  RefreshCw,
  Search,
  AlertCircle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ArrowRight,
  ChevronDown,
  Download,
  Lock,
  MoreHorizontal,
} from 'lucide-react';
import {
  apiService,
  getApiErrorMessage,
  Index,
  IndexDeletionResponse,
  MarkerInfo,
  MetadataExport,
  Video,
  VideoWithDetails,
  PromptRun,
  PromptRunFailedSegmentsManifest,
  PromptRunWorkflowVideoResult,
  Connector,
  ConnectorImportMode,
  ImportJob,
  ProcessingStreamEvent,
  VideoStatus,
} from '../services/api';
import { useTabState } from '../contexts/TabStateContext';
import { useTabActivity } from '../contexts/TabActivityContext';
import { useAuth } from '../contexts/AuthContext';
import { PlaygroundFeatureAccessProvider, useFeatureAccess } from '../contexts/FeatureAccessContext';
import { parseResultLink, ResultLinkTarget } from '../navigation/resultLinks';
import { commitOwnedSearch, HistoryMode } from '../navigation/historyState';
import { getExecuteLocation } from '../navigation/executeNavigation';
import { parseEnumParam } from '../navigation/urlCodec';
import VideoDetailModal from './VideoDetailModal';
import VideoTile from './VideoTile';
import { getDisplayedVideoStatus, selectVideoRun } from '../utils/runScopedVideoStatus';
import { DeferredPreviewTile, PreviewViewportProvider } from './GrantedPreviewImage';
import AddVideoSidebar, { AddVideoMode } from './AddVideoSidebar';
import { uploadQueue } from '../services/uploadQueue';
import { usePassiveApiActivity } from '../hooks/usePassiveApiActivity';
import { usePageVisibility } from '../hooks/usePageVisibility';
import { segmentCache } from '../services/segmentCache';
import {
  applyRealtimeStateToVideo,
  processingRealtimeStore,
} from '../services/processingRealtimeStore';
import {
  getPromptRunIndexId,
  sanitizeRunVideoIds,
  sortPromptRunsByExecutionDesc,
} from '../utils/promptRunProgress';
import {
  getManifestVideoFailureMap,
  isPromptRunTerminalStatus,
} from '../utils/promptRunFailures';
import { getExportDeliveryAuthority } from '../utils/exportDelivery';
import { toPromptRunFromProcessingEvent } from '../utils/promptRunEventMapper';
import { sortIndexes } from '../utils/indexSort';
import { sortVideos } from '../utils/videoSorting';
import { isInProgressStatus, isVideoInProgress } from '../utils/realtimeStreamEligibility';
import { describeInsufficientCredits, extractInsufficientCredits } from '../utils/billingErrors';
import { extractPlanUpgradeRequired } from '../utils/planUpgrade';
import {
  applyIndexDeletionStatus,
  clearIndexDeletionIfMatching,
  deriveUsableIndexRefreshState,
  excludeAcceptedIndexDeletions,
  reconcileUncertainIndexDeletion404s,
  recordAcceptedIndexDeletion,
  recordUncertainIndexDeletion,
} from '../utils/indexDeletionState';
import { useVideoDeletionPolling } from '../hooks/useVideoDeletionPolling';
import {
  excludeAcceptedVideoDeletions,
  PendingVideoDeletionMap,
  recordAcceptedVideoDeletion,
} from '../utils/videoDeletionState';
import {
  createErrorRecoveryAction,
  ErrorRecoveryAction,
  getVisibleErrorRecoveryAction,
} from '../utils/errorRecoveryAction';
import {
  AsyncRequestOwnershipToken,
  buildAsyncRequestOwnerKey,
  isCurrentAsyncRequest,
} from '../utils/asyncRequestOwnership';
import {
  applyVideoAssetsReadyEvent,
  projectVideoPreviewFields,
} from '../utils/videoPreviewProjection';
import {
  getImportHistoryStatusFallback,
  IngestionHistoryItem,
  mergeIngestionHistoryEntries,
  projectImportIngestionHistoryItem,
} from '../utils/ingestionHistory';
import {
  mergeEntityPagePreservingMutations,
  mergeEntitySnapshotPreservingMutations,
} from '../utils/entitySnapshotMerge';
import {
  clearPendingIdempotencyAttempt,
  getOrCreatePendingIdempotencyAttempt,
  PendingIdempotencyAttempt,
} from '../services/pendingIdempotencyAttempt';
import { isRetryableStreamRequestError } from '../services/idempotency';
import { promptRunHasSelectionFilter } from '../features/workflows/config';
import IndexAutomationControl from './IndexAutomationControl';
import './IndexTab.css';

const OWNED_KEYS = ['index', 'view', 'video', 'run', 'result', 't', 'field', 'ingest', 'vq', 'vsort', 'vdir', 'isort', 'idir', 'segment', 'output', 'playback', 'metadata'];
const RUN_VIDEO_PAGE_SIZE = 50;
const EMPTY_WORKFLOW_MATCH_SEGMENT_IDS: string[] = [];
const RUN_FAILURE_MANIFEST_DEBOUNCE_MS = 650;
const STATUS_RECONCILE_DEBOUNCE_MS = 300;
const EXPORT_STATUS_POLL_DELAYS_MS = [2000, 3000, 5000, 8000, 12000];
const EXPORT_STATUS_MAX_POLL_ATTEMPTS = 30;
const INDEX_LOAD_ERROR = 'Failed to load indexes';
const DEMO_INDEX_DISABLED_REASON = 'Demo indexes cannot be changed or processed. Create an index and upload your videos to process them.';
const INDEX_DELETION_RECONCILING_MESSAGE = 'Index deletion was submitted and is being reconciled.';
const INDEX_MUTATION_ATTEMPT_NAMESPACE_PREFIX = 'index-workspace';
type DatasetStatus = 'idle' | 'loading' | 'success' | 'empty' | 'error';
export type IndexView = 'list' | 'video' | 'add' | 'process' | 'export' | 'create';

export const getDefaultIndexResultMode = (
  _run: PromptRun | null | undefined,
): 'all' | 'matches' => 'all';

export const getIndexResultUrlParam = (
  selectedRunId: string | null,
  resultMode: 'all' | 'matches',
  isResolved: boolean,
): 'all' | 'matches' | null => selectedRunId && isResolved ? resultMode : null;

export const getIndexExportResultScope = (
  selectedRunId: string | null,
  isRunView: boolean,
  isMatchesView: boolean,
): { promptRunIds: string[]; resultScope: 'all' | 'matches' } => ({
  promptRunIds: isRunView && selectedRunId ? [selectedRunId] : [],
  resultScope: isRunView && isMatchesView ? 'matches' : 'all',
});

/** Playground is a UI alias; its persisted run scope is the authenticated user. */
export const getIndexPromptRunScopeId = (
  indexId: string,
  userId: string | null | undefined,
): string | null => indexId === 'playground' ? userId || null : indexId || null;

interface PromptRunsLoadOptions {
  background?: boolean;
  preserveExistingOnError?: boolean;
}

interface AuthScopedMutationAttempt {
  namespace: string;
  attempt: PendingIdempotencyAttempt;
}

interface VersionedVideoRealtimeEvent {
  revision: number;
  event: ProcessingStreamEvent;
}

export const isIndexPageVisibilityResume = (
  wasVisible: boolean,
  isVisible: boolean,
): boolean => isVisible && !wasVisible;

export const shouldSubscribeToIndexProcessingStream = (
  isVisible: boolean,
  indexId: string | null | undefined,
): boolean => Boolean(isVisible && indexId);

export interface IndexApiErrorPresentation {
  message: string;
  action: ErrorRecoveryAction | null;
}

/** Keep billing and plan recovery behavior consistent across Index-owned surfaces. */
export const getIndexApiErrorPresentation = (
  err: unknown,
  fallbackMessage: string,
): IndexApiErrorPresentation => {
  const insufficientCredits = extractInsufficientCredits(err);
  if (insufficientCredits) {
    const message = describeInsufficientCredits(insufficientCredits);
    return {
      message,
      action: createErrorRecoveryAction(message, {
        label: 'Add credits',
        path: insufficientCredits.purchasePath,
      }),
    };
  }

  const planUpgrade = extractPlanUpgradeRequired(err);
  if (planUpgrade) {
    const message = planUpgrade.message || 'This action requires a paid plan.';
    return {
      message,
      action: createErrorRecoveryAction(message, {
        label: 'View plans',
        path: planUpgrade.details.upgrade_path || '/billing?section=plans',
      }),
    };
  }

  return {
    message: getApiErrorMessage(err, fallbackMessage),
    action: null,
  };
};

const chunkIds = (ids: string[], chunkSize: number): string[][] => {
  if (!ids.length || chunkSize <= 0) {
    return [];
  }
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += chunkSize) {
    chunks.push(ids.slice(i, i + chunkSize));
  }
  return chunks;
};

interface MergeVideoStatusProjectionOptions {
  preserveTerminalStatus?: boolean;
}

const isTerminalVideoStatus = (status?: string): boolean => {
  const normalized = String(status || '').trim().toLowerCase();
  return normalized === 'processed' || normalized === 'failed';
};

/** Merge the authoritative status endpoint projection without replacing gallery fields. */
export const mergeVideoStatusProjections = (
  videos: Video[],
  statusProjections: VideoStatus[],
  options: MergeVideoStatusProjectionOptions = {},
): Video[] => {
  if (!videos.length || !statusProjections.length) {
    return videos;
  }

  const statusByVideoId = new Map(
    statusProjections.map((statusProjection) => [statusProjection.video_id, statusProjection]),
  );

  let changed = false;
  const mergedVideos = videos.map((video) => {
    const statusProjection = statusByVideoId.get(video.video_id);
    if (!statusProjection) {
      return video;
    }

    if (
      options.preserveTerminalStatus
      && isTerminalVideoStatus(video.status)
      && isInProgressStatus(statusProjection.status)
    ) {
      return video;
    }

    const nextStatus = statusProjection.status || video.status;
    const nextProcessingStatus = Array.isArray(statusProjection.processing_status)
      ? statusProjection.processing_status
      : (video.processing_status || []);
    if (
      nextStatus === video.status
      && JSON.stringify(nextProcessingStatus) === JSON.stringify(video.processing_status || [])
    ) {
      return video;
    }

    changed = true;
    return {
      ...video,
      status: nextStatus,
      processing_status: nextProcessingStatus,
    };
  });
  return changed ? mergedVideos : videos;
};

export const getVideoIdsMissingStatusProjection = (videos: Video[]): string[] => (
  videos
    .filter((video) => !Array.isArray(video.processing_status))
    .map((video) => video.video_id)
);

/** Backward-compatible Index export; all galleries share this projection. */
export const projectIndexVideoPreviewFields = projectVideoPreviewFields;
export { applyVideoAssetsReadyEvent } from '../utils/videoPreviewProjection';

interface RunScopedVideosState {
  runId: string;
  indexId: string;
  videoIds: string[];
  videos: Video[];
  loadedVideoIds: Record<string, true>;
  nextOffset: number;
  hasMore: boolean;
  isLoading: boolean;
  error: string | null;
}

interface WorkflowMatchVideosState {
  runId: string;
  indexId: string;
  videos: Video[];
  loadedVideoIds: Record<string, true>;
  matchesByVideoId: Record<string, Pick<PromptRunWorkflowVideoResult, 'video_matched' | 'matched_segment_ids'>>;
  nextCursor: string | null;
  hasMore: boolean;
  isLoading: boolean;
  error: string | null;
}

const IndexTab: React.FC = () => {
  const { indexTabState, setIndexTabState } = useTabState();
  const { isActive } = useTabActivity();
  const { currentUser } = useAuth();
  const { getLockedFeature, hasFeature } = useFeatureAccess();
  const navigate = useNavigate();
  const location = useLocation();
  const beginMutationAttempt = useCallback(async (
    operation: string,
    requestPayload: unknown,
    keyPrefix: string,
  ): Promise<AuthScopedMutationAttempt> => {
    const userId = String(currentUser?.uid || '').trim();
    if (!userId) {
      throw new Error('Your authenticated session is not available. Sign in and try again.');
    }
    const namespace = `${INDEX_MUTATION_ATTEMPT_NAMESPACE_PREFIX}:${userId}`;
    const attempt = await getOrCreatePendingIdempotencyAttempt({
      namespace,
      operation,
      requestPayload,
      keyPrefix,
    });
    return { namespace, attempt };
  }, [currentUser?.uid]);
  const clearMutationAttempt = useCallback((ownedAttempt: AuthScopedMutationAttempt) => {
    clearPendingIdempotencyAttempt({
      namespace: ownedAttempt.namespace,
      operation: ownedAttempt.attempt.operation,
      requestFingerprint: ownedAttempt.attempt.requestFingerprint,
      expectedIdempotencyKey: ownedAttempt.attempt.idempotencyKey,
    });
  }, []);
  const acceptedIndexDeletionIdsRef = useRef<Set<string>>(
    new Set(Object.keys(indexTabState.pendingIndexDeletions || {})),
  );
  const acceptedVideoDeletionIdsRef = useRef<Set<string>>(
    new Set(Object.keys(indexTabState.pendingVideoDeletions || {})),
  );
  const initialIndexes: Index[] = [];
  const initialSelectedIndex: Index | null = null;
  const initialVideos: Video[] = [];
  const [urlHydrated, setUrlHydrated] = useState(false);
  const [urlNotice, setUrlNotice] = useState<string | null>(null);
  const historyModeRef = useRef<HistoryMode>('replace');
  const isApplyingUrlRef = useRef(false);
  const resultModeResolvedFromUrlRef = useRef(false);
  const [isResultModeResolvedFromUrl, setIsResultModeResolvedFromUrl] = useState(false);
  const urlApplicationGenerationRef = useRef(0);
  const lastAppliedSearchRef = useRef<string | null>(null);
  const pendingIndexIdRef = useRef<string | null>(null);
  const pendingViewRef = useRef<'list' | 'video' | 'add' | 'process' | 'export' | 'create' | null>(null);
  const isApplyingPendingViewRef = useRef(false);
  const pendingVideoIdRef = useRef<string | null>(null);
  const pendingIngestModeRef = useRef<AddVideoMode>('upload');
  const [resultLink, setResultLink] = useState<ResultLinkTarget | null>(null);
  const [deepLinkRunId, setDeepLinkRunId] = useState<string | null>(null);
  const [deepLinkTime, setDeepLinkTime] = useState<number | null>(null);
  const [deepLinkFieldKey, setDeepLinkFieldKey] = useState<string | null>(null);
  const initialDataLoadStartedRef = useRef(false);
  const indexLoadRequestGenerationRef = useRef(0);
  const connectorLoadRequestGenerationRef = useRef(0);
  const videoLoadRequestGenerationRef = useRef(0);
  const selectedIndexGenerationRef = useRef(0);
  const videoFullLoadOwnerRef = useRef<symbol | null>(null);
  const videoLoadMoreOwnerRef = useRef<symbol | null>(null);
  const videoMutationRevisionRef = useRef(0);
  const videoMutationRevisionByIdRef = useRef<Map<string, number>>(new Map());
  const videoRealtimeEventsByIdRef = useRef<Map<string, VersionedVideoRealtimeEvent[]>>(new Map());
  const promptRunMutationRevisionRef = useRef(0);
  const promptRunMutationRevisionByIdRef = useRef<Map<string, number>>(new Map());
  const statusReconcileRequestRef = useRef(0);
  const videoStatusRequestsByIdRef = useRef<Map<string, Promise<VideoStatus | null>>>(new Map());
  const indexDeletionRequestsInFlightRef = useRef<Set<string>>(new Set());
  const loadIndexesRef = useRef<(
    (authoritativeIndexes?: Index[]) => Promise<Index[] | null>
  ) | null>(null);
  const indexesRef = useRef<Index[]>(initialIndexes);
  const [indexes, setIndexesState] = useState<Index[]>(() => initialIndexes);
  const setIndexes = useCallback(
    (nextValue: React.SetStateAction<Index[]>) => {
      const previous = indexesRef.current;
      const next = typeof nextValue === 'function'
        ? nextValue(previous)
        : nextValue;
      const usableIndexes = excludeAcceptedIndexDeletions(
        next,
        acceptedIndexDeletionIdsRef.current,
      );
      indexesRef.current = usableIndexes;
      setIndexesState(usableIndexes);
    },
    [],
  );
  const [indexesLoaded, setIndexesLoaded] = useState(false);
  const selectedIndexRef = useRef<Index | null>(initialSelectedIndex);
  const [selectedIndex, setSelectedIndexState] = useState<Index | null>(() => initialSelectedIndex);
  const setSelectedIndex = useCallback(
    (nextValue: React.SetStateAction<Index | null>) => {
      const previous = selectedIndexRef.current;
      const next = typeof nextValue === 'function'
        ? nextValue(previous)
        : nextValue;
      if (previous?.index_id !== next?.index_id) {
        selectedIndexGenerationRef.current += 1;
        videoLoadRequestGenerationRef.current += 1;
        statusReconcileRequestRef.current += 1;
        videoFullLoadOwnerRef.current = null;
        videoLoadMoreOwnerRef.current = null;
        const preserveRequestedRun = (
          pendingIndexIdRef.current === next?.index_id
          && pendingViewRef.current !== 'video'
        );
        setIndexTabState((current) => ({
          ...current,
          selectedPromptRunId: preserveRequestedRun
            ? current.selectedPromptRunId
            : null,
          resultMode: preserveRequestedRun ? current.resultMode : 'all',
        }));
      }
      selectedIndexRef.current = next;
      setSelectedIndexState(next);
    },
    [setIndexTabState],
  );
  const ownsIndexSelection = useCallback((indexId: string, generation: number): boolean => (
    selectedIndexGenerationRef.current === generation
    && selectedIndexRef.current?.index_id === indexId
  ), []);
  const [openIndexMenuId, setOpenIndexMenuId] = useState<string | null>(null);
  const videosRef = useRef<Video[]>(initialVideos);
  const videosIndexIdRef = useRef<string | null>(null);
  const [videos, setVideosState] = useState<Video[]>(() => initialVideos);
  const setVideos = useCallback(
    (nextValue: React.SetStateAction<Video[]>) => {
      const previous = videosRef.current;
      const next = typeof nextValue === 'function'
        ? nextValue(previous)
        : nextValue;
      const usableVideos = excludeAcceptedVideoDeletions(
        next,
        acceptedVideoDeletionIdsRef.current,
      ).map((video) => ({
        ...video,
        ...projectIndexVideoPreviewFields(video),
      }));
      videosRef.current = usableVideos;
      setVideosState(usableVideos);
    },
    [],
  );
  const recordVideoMutations = useCallback((videoIds: Iterable<string>): number | null => {
    const normalizedIds = Array.from(new Set(
      Array.from(videoIds, (videoId) => String(videoId || '').trim()).filter(Boolean),
    ));
    if (!normalizedIds.length) return null;
    const revision = videoMutationRevisionRef.current + 1;
    videoMutationRevisionRef.current = revision;
    normalizedIds.forEach((videoId) => {
      videoMutationRevisionByIdRef.current.set(videoId, revision);
    });
    return revision;
  }, []);
  const publishVideoMutation = useCallback((
    videoIds: Iterable<string>,
    nextValue: React.SetStateAction<Video[]>,
    realtimeEvent?: ProcessingStreamEvent,
  ) => {
    const normalizedIds = Array.from(new Set(
      Array.from(videoIds, (videoId) => String(videoId || '').trim()).filter(Boolean),
    ));
    const revision = recordVideoMutations(normalizedIds);
    if (realtimeEvent && revision !== null) {
      normalizedIds.forEach((videoId) => {
        const retained = videoRealtimeEventsByIdRef.current.get(videoId) || [];
        videoRealtimeEventsByIdRef.current.set(videoId, [
          ...retained.slice(-7),
          { revision, event: realtimeEvent },
        ]);
      });
    }
    setVideos(nextValue);
  }, [recordVideoMutations, setVideos]);
  const recordPromptRunMutation = useCallback((runId: string) => {
    const normalizedRunId = String(runId || '').trim();
    if (!normalizedRunId) return;
    const revision = promptRunMutationRevisionRef.current + 1;
    promptRunMutationRevisionRef.current = revision;
    promptRunMutationRevisionByIdRef.current.set(normalizedRunId, revision);
  }, []);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingVideos, setIsLoadingVideos] = useState(false);
  const [videoDatasetStatus, setVideoDatasetStatus] = useState<DatasetStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [errorAction, setErrorAction] = useState<ErrorRecoveryAction | null>(null);
  const currentUserIdRef = useRef<string | null>(currentUser?.uid || null);
  currentUserIdRef.current = currentUser?.uid || null;
  const [selectedVideoForDetail, setSelectedVideoForDetail] = useState<Video | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const promptRunsRef = useRef<PromptRun[]>([]);
  const [promptRuns, setPromptRunsState] = useState<PromptRun[]>([]);
  const setPromptRuns = useCallback(
    (nextValue: React.SetStateAction<PromptRun[]>) => {
      const previous = promptRunsRef.current;
      const next = typeof nextValue === 'function'
        ? nextValue(previous)
        : nextValue;
      promptRunsRef.current = next;
      setPromptRunsState(next);
    },
    [],
  );
  const promptRunsIndexIdRef = useRef<string | null>(null);
  const [promptRunsIndexId, setPromptRunsIndexIdState] = useState<string | null>(null);
  const setPromptRunsIndexId = useCallback((nextIndexId: string | null) => {
    promptRunsIndexIdRef.current = nextIndexId;
    setPromptRunsIndexIdState(nextIndexId);
  }, []);
  const [isLoadingPromptRuns, setIsLoadingPromptRuns] = useState(false);
  const [promptRunsError, setPromptRunsError] = useState<string | null>(null);
  const [runFailureManifestByRunId, setRunFailureManifestByRunId] = useState<Record<string, PromptRunFailedSegmentsManifest>>({});
  const [runVideosByRunId, setRunVideosByRunId] = useState<Record<string, RunScopedVideosState>>({});
  const runVideosByRunIdRef = useRef<Record<string, RunScopedVideosState>>({});
  const [workflowMatchVideosByRunId, setWorkflowMatchVideosByRunId] = useState<Record<string, WorkflowMatchVideosState>>({});
  const workflowMatchVideosByRunIdRef = useRef<Record<string, WorkflowMatchVideosState>>({});
  const workflowMatchRequestVersionRef = useRef<Record<string, number>>({});
  const promptRunsLoadGenerationRef = useRef(0);
  const promptRunsLoadInFlightRef = useRef<Map<string, {
    selectionGeneration: number;
    request: Promise<PromptRun[] | null>;
  }>>(new Map());
  const [exportDestinationType, setExportDestinationType] = useState<'download' | 'connector'>('download');
  const [selectedExportConnectorId, setSelectedExportConnectorId] = useState<string>('');
  const [exportDestinationSubpath, setExportDestinationSubpath] = useState<string>('');
  const [isExporting, setIsExporting] = useState(false);
  const exportSubmissionInFlightRef = useRef(false);
  const [exportSuccess, setExportSuccess] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportErrorAction, setExportErrorAction] = useState<ErrorRecoveryAction | null>(null);
  const [previousExports, setPreviousExports] = useState<MetadataExport[]>([]);
  const [isLoadingExports, setIsLoadingExports] = useState(false);
  const [exportHistoryError, setExportHistoryError] = useState<string | null>(null);
  const exportStatusPollTimerRef = useRef<number | null>(null);
  const exportPollResumeRef = useRef<(() => void) | null>(null);
  const exportStatusPollGenerationRef = useRef(0);
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    const saved = localStorage.getItem('videoSidebarWidth');
    return saved ? parseInt(saved, 10) : 900;
  });
  const [addVideoSidebarWidth, setAddVideoSidebarWidth] = useState<number>(() => {
    const saved = localStorage.getItem('addVideoSidebarWidth');
    return saved ? parseInt(saved, 10) : 820;
  });
  // Ingestion sidebar state
  const [addVideoMode, setAddVideoMode] = useState<AddVideoMode>('upload');
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const connectorsRef = useRef<Connector[]>([]);
  const [selectedConnectorId, setSelectedConnectorId] = useState<string>('');
  const [importSourcePrefix, setImportSourcePrefix] = useState('');
  const [importFilePattern, setImportFilePattern] = useState('*');
  const [importRecursive, setImportRecursive] = useState(true);
  const [importModeOverride, setImportModeOverride] = useState<'' | ConnectorImportMode>('');
  const [isLoadingConnectors, setIsLoadingConnectors] = useState(false);
  const [isCreatingImportJob, setIsCreatingImportJob] = useState(false);
  const importCreationInFlightRef = useRef(false);
  const [isRefreshingIngestionHistory, setIsRefreshingIngestionHistory] = useState(false);
  const ingestionHistoryReadGenerationRef = useRef(0);
  const ingestionHistoryReadInFlightRef = useRef<Promise<ImportJob[]> | null>(null);
  const [showTileDetails, setShowTileDetails] = useState<boolean>(() => {
    const saved = localStorage.getItem('showTileDetails');
    return saved ? JSON.parse(saved) : false;
  });
  const [tilesPerRow, setTilesPerRow] = useState<number>(() => {
    const saved = localStorage.getItem('tilesPerRow');
    return saved ? parseInt(saved, 10) : 6;
  });
  const runFailureManifestTimersRef = useRef<Map<string, number>>(new Map());
  const runFailureManifestRequestVersionRef = useRef<Record<string, number>>({});
  const runVideosRequestVersionRef = useRef<Record<string, number>>({});
  const videoControlsRef = useRef<HTMLDivElement>(null);
  const indexMainRef = useRef<HTMLDivElement>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);

  // Server-side pagination state
  const [videosNextCursor, setVideosNextCursor] = useState<string | null>(null);
  const [hasMoreVideos, setHasMoreVideos] = useState(false);
  const [isLoadingMoreVideos, setIsLoadingMoreVideos] = useState(false);
  const isPageVisible = usePageVisibility();
  const canObserve = usePassiveApiActivity(
    promptRuns.some((run) => isInProgressStatus(run.status)) || videos.some((video) => isVideoInProgress(video)),
  );
  const isActiveAndVisible = isActive && isPageVisible && canObserve;
  const canObserveRef = useRef(isActiveAndVisible);
  canObserveRef.current = isActiveAndVisible;
  const previousPageVisibilityRef = useRef(isPageVisible);
  const previousActiveVisibilityRef = useRef(isActiveAndVisible);
  const connectorLockedFeature = getLockedFeature('connector_integrations');
  const importLockedFeature = getLockedFeature('import_lifecycle') || connectorLockedFeature;
  const exportLockedFeature = getLockedFeature('export_lifecycle');
  const mutationLockedFeature = getLockedFeature('workspace_mutation');
  const canMutateWorkspace = !mutationLockedFeature;
  const canUseConnectors = hasFeature('connector_integrations');
  const canUseImportLifecycle = !importLockedFeature;
  const canUseExportLifecycle = !exportLockedFeature;
  connectorsRef.current = connectors;

  const importCapableConnectors = useMemo(
    () => connectors.filter((connector) => {
      const scopes = connector.scopes && connector.scopes.length > 0 ? connector.scopes : ['import'];
      return connector.status === 'active' && scopes.includes('import');
    }),
    [connectors]
  );

  const exportCapableConnectors = useMemo(
    () => connectors.filter((connector) => {
      const scopes = connector.scopes && connector.scopes.length > 0 ? connector.scopes : ['import'];
      return connector.status === 'active' && scopes.includes('export');
    }),
    [connectors]
  );
  const cancelExportStatusPolling = useCallback(() => {
    exportStatusPollGenerationRef.current += 1;
    exportPollResumeRef.current = null;
    if (exportStatusPollTimerRef.current !== null) {
      window.clearTimeout(exportStatusPollTimerRef.current);
      exportStatusPollTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isActiveAndVisible) {
      if (exportStatusPollTimerRef.current !== null) window.clearTimeout(exportStatusPollTimerRef.current);
      exportStatusPollTimerRef.current = null;
    } else {
      exportPollResumeRef.current?.();
    }
  }, [isActiveAndVisible]);

  const resetExportModalState = useCallback(() => {
    historyModeRef.current = 'push';
    cancelExportStatusPolling();
    setShowExportModal(false);
    setExportDestinationType('download');
    setSelectedExportConnectorId('');
    setExportDestinationSubpath('');
    setIsExporting(exportSubmissionInFlightRef.current);
    setExportSuccess(null);
    setExportError(null);
    setExportErrorAction(null);
    setPreviousExports([]);
    setExportHistoryError(null);
  }, [cancelExportStatusPolling]);

  useEffect(() => () => cancelExportStatusPolling(), [cancelExportStatusPolling]);

  const selectedPromptRunIdForExport = indexTabState.selectedPromptRunId;
  useEffect(() => {
    cancelExportStatusPolling();
    if (!showExportModal) return;

    setPreviousExports([]);
    setExportDestinationType('download');
    setSelectedExportConnectorId('');
    setExportDestinationSubpath('');
    setIsExporting(exportSubmissionInFlightRef.current);
    setExportSuccess(null);
    setExportError(null);
    setExportErrorAction(null);
    setExportHistoryError(null);
  }, [cancelExportStatusPolling, selectedIndex?.index_id, selectedPromptRunIdForExport, showExportModal]);

  useEffect(() => {
    if (exportDestinationType !== 'connector') return;
    if (selectedExportConnectorId) return;
    if (exportCapableConnectors.length === 0) return;
    setSelectedExportConnectorId(exportCapableConnectors[0].connector_id);
  }, [exportDestinationType, selectedExportConnectorId, exportCapableConnectors]);

  // Use persistent state from context
  const {
    selectedIndexId,
    selectedPromptRunId,
    resultMode,
    sortBy,
    sortAsc,
    showCreateModal,
    showAddVideoModal,
    newIndexName,
    videoSearchQuery,
    videoSortBy,
    videoSortAsc,
    pendingIndexDeletions: persistedPendingIndexDeletions,
    pendingVideoDeletions: persistedPendingVideoDeletions,
  } = indexTabState;
  const selectedIndexIdRef = useRef<string | null>(selectedIndexId);
  selectedIndexIdRef.current = selectedIndexId;
  const selectedPromptRunIdRef = useRef<string | null>(selectedPromptRunId);
  selectedPromptRunIdRef.current = selectedPromptRunId;
  const pendingIndexDeletions = useMemo<
    NonNullable<typeof indexTabState.pendingIndexDeletions>
  >(
    () => persistedPendingIndexDeletions || {},
    [persistedPendingIndexDeletions],
  );
  const pendingVideoDeletions = useMemo<PendingVideoDeletionMap>(
    () => persistedPendingVideoDeletions || {},
    [persistedPendingVideoDeletions],
  );

  const updatePendingVideoDeletions = useCallback(
    (
      updater: (
        current: PendingVideoDeletionMap,
      ) => PendingVideoDeletionMap,
    ) => {
      setIndexTabState((previous) => {
        const current = previous.pendingVideoDeletions || {};
        const next = updater(current);
        return next === current
          ? previous
          : { ...previous, pendingVideoDeletions: next };
      });
    },
    [setIndexTabState],
  );
  useVideoDeletionPolling(
    pendingVideoDeletions,
    updatePendingVideoDeletions,
  );

  const ingestionHistory = indexTabState.ingestionHistory as IngestionHistoryItem[];
  const ingestionHistoryLastSeenAt = indexTabState.ingestionHistoryLastSeenAt;
  const isRestoringSelectedIndex = !selectedIndex && (!indexesLoaded || isLoading);
  
  // Helper function to update state
  const updateState = useCallback(
    (updates: Partial<typeof indexTabState>) => {
      setIndexTabState(prev => ({ ...prev, ...updates }));
    },
    [setIndexTabState],
  );

  const deletionWasObservingRef = useRef(isActiveAndVisible);
  useEffect(() => {
    const resumed = isActiveAndVisible && !deletionWasObservingRef.current;
    deletionWasObservingRef.current = isActiveAndVisible;
    const pendingEntries = Object.values(pendingIndexDeletions);
    if (!isActiveAndVisible || pendingEntries.length === 0) {
      return;
    }

    let cancelled = false;
    let timer: number | undefined;
    const poll = async () => {
      const snapshot = Object.entries(pendingIndexDeletions);
      const results = await Promise.all(
        snapshot.map(async ([indexId, deletion]) => {
          try {
            return {
              kind: 'status' as const,
              response: await apiService.getIndexDeletion(indexId),
            };
          } catch (pollError) {
            const httpStatus = (pollError as any)?.response?.status;
            if (httpStatus === 404 || httpStatus === 410) {
              return {
                kind: 'gone' as const,
                indexId,
                deletionId: deletion.deletion_id,
                restoreIfUncertain: httpStatus === 404 && Boolean(deletion.outcome_uncertain),
              };
            }
            console.warn('Unable to refresh index deletion status:', pollError);
            return { kind: 'retry' as const };
          }
        }),
      );
      if (cancelled) {
        return;
      }

      setIndexTabState((previous) => {
        const current = previous.pendingIndexDeletions || {};
        const next = results.reduce(
          (state, result) => {
            if (result.kind === 'status') {
              return applyIndexDeletionStatus(state, result.response);
            }
            if (result.kind === 'gone') {
              if (result.restoreIfUncertain) {
                return state;
              }
              return clearIndexDeletionIfMatching(
                state,
                result.indexId,
                result.deletionId,
              );
            }
            return state;
          },
          current,
        );
        return next === current
          ? previous
          : { ...previous, pendingIndexDeletions: next };
      });

      const uncertainIndexIds = new Set(
        snapshot
          .filter(([, deletion]) => deletion.outcome_uncertain)
          .map(([indexId]) => indexId),
      );
      if (uncertainIndexIds.size > 0) {
        const resolvedUncertainOutcome = results.some(
          (result) => (
            result.kind === 'status'
              ? uncertainIndexIds.has(result.response.index_id)
              : (
                result.kind === 'gone'
                && !result.restoreIfUncertain
                && uncertainIndexIds.has(result.indexId)
              )
          ),
        );
        if (resolvedUncertainOutcome) {
          setError((previousError) => (
            previousError === INDEX_DELETION_RECONCILING_MESSAGE
              ? null
              : previousError
          ));
        }
      }

      const uncertainMissingEntries = results.flatMap((result) => (
        result.kind === 'gone' && result.restoreIfUncertain
          ? [{
              indexId: result.indexId,
              deletionId: result.deletionId,
            }]
          : []
      ));
      if (uncertainMissingEntries.length > 0) {
        const reconciliation = await reconcileUncertainIndexDeletion404s(
          uncertainMissingEntries.map(({ indexId }) => indexId),
          () => apiService.getIndexes(),
        );
        if (cancelled) {
          return;
        }
        if (reconciliation.status === 'retry') {
          setError(INDEX_DELETION_RECONCILING_MESSAGE);
        } else {
          reconciliation.presentIndexIds.forEach((indexId) => {
            acceptedIndexDeletionIdsRef.current.delete(indexId);
          });
          reconciliation.absentIndexIds.forEach((indexId) => {
            acceptedIndexDeletionIdsRef.current.add(indexId);
          });
          void loadIndexesRef.current?.(reconciliation.indexes);
          setIndexTabState((previous) => {
            const current = previous.pendingIndexDeletions || {};
            const next = uncertainMissingEntries.reduce(
              (state, { indexId, deletionId }) => (
                clearIndexDeletionIfMatching(
                  state,
                  indexId,
                  deletionId,
                )
              ),
              current,
            );
            return next === current
              ? previous
              : { ...previous, pendingIndexDeletions: next };
          });
          setError((previousError) => (
            previousError === INDEX_DELETION_RECONCILING_MESSAGE
              ? null
              : previousError
          ));
        }
      }

      const retrySeconds = Math.min(
        ...Object.values(pendingIndexDeletions).map((entry) => (
          Math.max(1, Math.min(entry.retry_after_seconds || 60, 300))
        )),
      );
      timer = window.setTimeout(poll, retrySeconds * 1000);
    };

    const initialRetrySeconds = Math.min(
      ...pendingEntries.map((entry) => (
        Math.max(1, Math.min(entry.retry_after_seconds || 60, 300))
      )),
    );
    if (resumed) void poll();
    else timer = window.setTimeout(poll, initialRetrySeconds * 1000);
    return () => {
      cancelled = true;
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    };
  }, [isActiveAndVisible, pendingIndexDeletions, setIndexTabState]);

  const setApiError = (err: unknown, fallbackMessage: string) => {
    const presentation = getIndexApiErrorPresentation(err, fallbackMessage);
    setError(presentation.message);
    setErrorAction(presentation.action);
  };

  const setExportApiError = (err: unknown, fallbackMessage: string) => {
    const presentation = getIndexApiErrorPresentation(err, fallbackMessage);
    setExportError(presentation.message);
    setExportErrorAction(presentation.action);
  };

  useEffect(() => {
    if (errorAction && errorAction.errorMessage !== error) {
      setErrorAction(null);
    }
  }, [error, errorAction]);


  const visibleErrorAction = getVisibleErrorRecoveryAction(error, errorAction);
  const visibleExportErrorAction = getVisibleErrorRecoveryAction(exportError, exportErrorAction);
  const runDatasetIndexRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    const indexId = selectedIndex?.index_id || null;
    if (runDatasetIndexRef.current === indexId) return;
    runDatasetIndexRef.current = indexId;
    runVideosRequestVersionRef.current = {};
    workflowMatchRequestVersionRef.current = {};
    setRunVideosByRunId({});
    setWorkflowMatchVideosByRunId({});
  }, [selectedIndex?.index_id]);

  useEffect(() => {
    runVideosByRunIdRef.current = runVideosByRunId;
  }, [runVideosByRunId]);

  useEffect(() => {
    workflowMatchVideosByRunIdRef.current = workflowMatchVideosByRunId;
  }, [workflowMatchVideosByRunId]);

  const clearRunFailureManifestTimers = useCallback(() => {
    runFailureManifestTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    runFailureManifestTimersRef.current.clear();
  }, []);

  useEffect(() => {
    return () => {
      clearRunFailureManifestTimers();
    };
  }, [clearRunFailureManifestTimers]);

  const upsertPromptRunEntry = useCallback((currentRuns: PromptRun[], incomingRun: PromptRun): PromptRun[] => {
    const byRunId = new Map(currentRuns.map((run) => [run.run_id, run]));
    const existing = byRunId.get(incomingRun.run_id);
    byRunId.set(incomingRun.run_id, {
      ...existing,
      ...incomingRun,
      run_context: incomingRun.run_context || existing?.run_context || {},
    });
    return sortPromptRunsByExecutionDesc(Array.from(byRunId.values()));
  }, []);
  const publishPromptRunMutation = useCallback((
    incomingRun: PromptRun,
    ownerIndexId?: string | null,
  ) => {
    recordPromptRunMutation(incomingRun.run_id);
    const normalizedOwnerIndexId = String(ownerIndexId || '').trim();
    if (
      normalizedOwnerIndexId
      && promptRunsIndexIdRef.current !== normalizedOwnerIndexId
    ) {
      setPromptRuns([incomingRun]);
      setPromptRunsIndexId(normalizedOwnerIndexId);
      return;
    }
    setPromptRuns((currentRuns) => upsertPromptRunEntry(currentRuns, incomingRun));
    if (normalizedOwnerIndexId) {
      setPromptRunsIndexId(normalizedOwnerIndexId);
    }
  }, [
    recordPromptRunMutation,
    setPromptRuns,
    setPromptRunsIndexId,
    upsertPromptRunEntry,
  ]);

  const eventMatchesSelectedIndex = useCallback((event: ProcessingStreamEvent, indexId: string): boolean => {
    const scopeId = getIndexPromptRunScopeId(indexId, currentUserIdRef.current);
    if (!scopeId) {
      return false;
    }

    if (event.index_id === scopeId) {
      return true;
    }

    const payload = event.payload || {};
    if (payload.index_id === scopeId) {
      return true;
    }

    const target = payload.target;
    if (target && typeof target === 'object' && target.index_id === scopeId) {
      return true;
    }

    return false;
  }, []);

  /**
   * One component-scoped gateway for status projections. Overlapping callers
   * share per-video promises, while completed requests are never cached as
   * processing state can change at any time.
   */
  const loadVideoStatusProjections = useCallback(async (videoIds: string[]): Promise<VideoStatus[]> => {
    const normalizedVideoIds = Array.from(new Set(
      videoIds.map((videoId) => String(videoId || '').trim()).filter(Boolean),
    ));
    if (!normalizedVideoIds.length) {
      return [];
    }

    const requestsById = videoStatusRequestsByIdRef.current;
    const missingVideoIds = normalizedVideoIds.filter((videoId) => !requestsById.has(videoId));

    chunkIds(missingVideoIds, 100).forEach((idsChunk) => {
      const chunkByIdPromise = apiService.getBatchVideoStatus(idsChunk).then(
        (statusProjections) => new Map(
          statusProjections.map((statusProjection) => [statusProjection.video_id, statusProjection]),
        ),
      );

      idsChunk.forEach((videoId) => {
        const statusPromise = chunkByIdPromise.then(
          (statusById) => statusById.get(videoId) || null,
        );
        requestsById.set(videoId, statusPromise);
        void statusPromise.then(
          () => {
            if (requestsById.get(videoId) === statusPromise) {
              requestsById.delete(videoId);
            }
          },
          () => {
            if (requestsById.get(videoId) === statusPromise) {
              requestsById.delete(videoId);
            }
          },
        );
      });
    });

    const statusProjections = await Promise.all(
      normalizedVideoIds.map((videoId) => requestsById.get(videoId) as Promise<VideoStatus | null>),
    );
    return statusProjections.filter(
      (statusProjection): statusProjection is VideoStatus => statusProjection !== null,
    );
  }, []);

  const hydrateVideoStatusProjections = useCallback(async (videoPage: Video[]): Promise<Video[]> => {
    if (!videoPage.length) {
      return videoPage;
    }
    const omittedStatusVideoIds = getVideoIdsMissingStatusProjection(videoPage);
    if (!omittedStatusVideoIds.length) {
      return videoPage;
    }
    try {
      const statusProjections = await loadVideoStatusProjections(omittedStatusVideoIds);
      return mergeVideoStatusProjections(videoPage, statusProjections);
    } catch (statusError) {
      // Status detail is an enhancement to an otherwise valid gallery page.
      // VideoTile falls back to its aggregate status rather than showing a
      // fabricated empty segment row.
      console.warn('Failed to hydrate video tile status details:', statusError);
      return videoPage;
    }
  }, [loadVideoStatusProjections]);

  const refreshRunFailureManifest = useCallback(async (runId: string) => {
    const stableRunId = String(runId || '').trim();
    if (!stableRunId) {
      return;
    }

    const requestVersion = (runFailureManifestRequestVersionRef.current[stableRunId] || 0) + 1;
    runFailureManifestRequestVersionRef.current[stableRunId] = requestVersion;
    try {
      const manifest = await apiService.getPromptRunFailedSegments(stableRunId);
      if (runFailureManifestRequestVersionRef.current[stableRunId] !== requestVersion) {
        return;
      }
      setRunFailureManifestByRunId((current) => ({
        ...current,
        [stableRunId]: manifest,
      }));
    } catch (manifestError: any) {
      if (runFailureManifestRequestVersionRef.current[stableRunId] !== requestVersion) {
        return;
      }
      console.warn(`Failed to load failed-segment manifest for run ${stableRunId}:`, manifestError);
      const status = manifestError?.response?.status;
      if (status === 404 || status === 403) {
        setRunFailureManifestByRunId((current) => {
          const next = { ...current };
          delete next[stableRunId];
          return next;
        });
      }
    }
  }, []);

  const scheduleRunFailureManifestRefresh = useCallback((runId?: string | null, delayMs: number = RUN_FAILURE_MANIFEST_DEBOUNCE_MS) => {
    const stableRunId = String(runId || '').trim();
    if (!stableRunId) {
      return;
    }
    const existingTimer = runFailureManifestTimersRef.current.get(stableRunId);
    if (existingTimer) {
      window.clearTimeout(existingTimer);
    }
    const timerId = window.setTimeout(() => {
      runFailureManifestTimersRef.current.delete(stableRunId);
      void refreshRunFailureManifest(stableRunId);
    }, Math.max(0, delayMs));
    runFailureManifestTimersRef.current.set(stableRunId, timerId);
  }, [refreshRunFailureManifest]);

  const upsertIngestionHistoryEntries = useCallback((entries: IngestionHistoryItem[]) => {
    if (!entries.length) return;

    setIndexTabState(prev => {
      const existing = prev.ingestionHistory as IngestionHistoryItem[];
      const merged = mergeIngestionHistoryEntries(existing, entries);

      return {
        ...prev,
        ingestionHistory: merged,
      };
    });
  }, [setIndexTabState]);

  const closeAddVideoSidebar = useCallback(() => {
    historyModeRef.current = 'push';
    updateState({
      showAddVideoModal: false,
      ingestionHistoryLastSeenAt: new Date().toISOString(),
    });
    setAddVideoMode('upload');
    setSelectedConnectorId('');
    setImportSourcePrefix('');
    setImportFilePattern('*');
    setImportRecursive(true);
  }, [updateState]);

  useEffect(() => {
    if (!isActive || !showAddVideoModal || addVideoMode !== 'upload' || !selectedIndex || selectedIndex.is_default || mutationLockedFeature) return;
    uploadQueue.open(selectedIndex.index_id, selectedIndex.name);
    closeAddVideoSidebar();
  }, [addVideoMode, closeAddVideoSidebar, isActive, mutationLockedFeature, selectedIndex, showAddVideoModal]);

  const openAddVideoSidebar = () => {
    if (selectedIndexRef.current?.is_default) return;
    historyModeRef.current = 'push';
    setSelectedVideoForDetail(null);
    updateState({ showAddVideoModal: true });
  };

  const handleAddVideoModeChange = (mode: AddVideoMode) => {
    setAddVideoMode(mode);
  };

  useEffect(() => {
    if (!isActive || lastAppliedSearchRef.current === location.search) return;
    const urlGeneration = ++urlApplicationGenerationRef.current;
    isApplyingUrlRef.current = true;
    isApplyingPendingViewRef.current = false;
    const params = new URLSearchParams(location.search);
    setResultLink(parseResultLink('/index', location.search));
    pendingIndexIdRef.current = params.get('index');
    const requestedView = parseEnumParam(
      params.get('view'),
      ['list', 'video', 'add', 'process', 'export', 'create'] as const,
      'list'
    );
    pendingViewRef.current = requestedView;
    pendingVideoIdRef.current = params.get('video');
    pendingIngestModeRef.current = parseEnumParam(params.get('ingest'), ['upload', 'import'] as const, 'upload');
    const hasRunParam = params.has('run');
    const hasResultParam = params.has('result');
    resultModeResolvedFromUrlRef.current = hasResultParam;
    setIsResultModeResolvedFromUrl(hasResultParam);
    const runParam = params.get('run') || null;
    setDeepLinkRunId(requestedView === 'video' ? runParam : null);
    const rawTime = params.get('t');
    const parsedTime = rawTime ? Number.parseFloat(rawTime) : NaN;
    setDeepLinkTime(Number.isFinite(parsedTime) ? parsedTime : null);
    setDeepLinkFieldKey(params.get('field') || null);

    const sharedUrlState = {
      resultMode: parseEnumParam(params.get('result'), ['all', 'matches'] as const, 'all'),
      videoSearchQuery: params.get('vq') || '',
      videoSortBy: parseEnumParam(params.get('vsort'), ['name', 'date', 'status'] as const, 'date'),
      videoSortAsc: parseEnumParam(params.get('vdir'), ['asc', 'desc'] as const, 'desc') === 'asc',
      sortBy: parseEnumParam(params.get('isort'), ['name', 'date'] as const, 'date'),
      sortAsc: parseEnumParam(params.get('idir'), ['asc', 'desc'] as const, 'desc') === 'asc',
    };

    updateState(
      requestedView !== 'video' || hasResultParam
        ? { ...sharedUrlState, selectedPromptRunId: hasRunParam ? runParam : null }
        : { ...sharedUrlState, selectedPromptRunId: null }
    );

    const timer = window.setTimeout(() => {
      lastAppliedSearchRef.current = location.search;
      isApplyingUrlRef.current = false;
      setUrlHydrated(true);
    }, 0);

    return () => {
      window.clearTimeout(timer);
      if (urlApplicationGenerationRef.current === urlGeneration) {
        urlApplicationGenerationRef.current += 1;
        isApplyingUrlRef.current = false;
        isApplyingPendingViewRef.current = false;
      }
    };
  }, [isActive, location.search, updateState]);

  useEffect(() => {
    if (!isActive || !openIndexMenuId) return;

    const handleWindowClick = () => {
      setOpenIndexMenuId(null);
    };

    const handleWindowKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenIndexMenuId(null);
      }
    };

    window.addEventListener('click', handleWindowClick);
    window.addEventListener('keydown', handleWindowKeyDown);

    return () => {
      window.removeEventListener('click', handleWindowClick);
      window.removeEventListener('keydown', handleWindowKeyDown);
    };
  }, [isActive, openIndexMenuId]);

  useEffect(() => {
    if (openIndexMenuId && !indexes.some(index => index.index_id === openIndexMenuId)) {
      setOpenIndexMenuId(null);
    }
  }, [indexes, openIndexMenuId]);

  // Listen for sidebar width changes
  useEffect(() => {
    if (!isActive) return;
    const handleSidebarWidthChange = (event: CustomEvent) => {
      setSidebarWidth(event.detail.width);
    };

    const handleAddVideoSidebarWidthChange = (event: CustomEvent) => {
      setAddVideoSidebarWidth(event.detail.width);
    };

    window.addEventListener('sidebarWidthChange' as any, handleSidebarWidthChange);
    window.addEventListener('addVideoSidebarWidthChange' as any, handleAddVideoSidebarWidthChange);

    return () => {
      window.removeEventListener('sidebarWidthChange' as any, handleSidebarWidthChange);
      window.removeEventListener('addVideoSidebarWidthChange' as any, handleAddVideoSidebarWidthChange);
    };
  }, [isActive]);

  // Never render detail or run-local state under a different index header.
  useLayoutEffect(() => {
    videosIndexIdRef.current = null;
    setSelectedVideoForDetail(null);
    setShowExportModal(false);
    setRunVideosByRunId({});
    setRunFailureManifestByRunId({});
  }, [selectedIndex?.index_id]);

  // Save showTileDetails to localStorage
  useEffect(() => {
    localStorage.setItem('showTileDetails', JSON.stringify(showTileDetails));
  }, [showTileDetails]);

  // Save tilesPerRow to localStorage
  useEffect(() => {
    localStorage.setItem('tilesPerRow', tilesPerRow.toString());
  }, [tilesPerRow]);
  
  // Sync selectedIndex with selectedIndexId from persistent state
  useEffect(() => {
    if (!isActive) return;
    const urlIndexId = new URLSearchParams(location.search).get('index');
    if (urlIndexId) return;

    if (selectedIndexId && indexes.length > 0) {
      const index = indexes.find(idx => idx.index_id === selectedIndexId);
      if (index && (!selectedIndex || selectedIndex.index_id !== index.index_id)) {
        setSelectedIndex(index);
      }
    }
  }, [isActive, location.search, selectedIndexId, indexes, selectedIndex, setSelectedIndex]);

  useEffect(() => {
    if (!isActive || !urlHydrated || indexes.length === 0) return;
    const requestedIndexId = pendingIndexIdRef.current;
    if (!requestedIndexId) return;

    const index = indexes.find((item) => item.index_id === requestedIndexId);
    if (index) {
      setSelectedIndex(index);
      selectedIndexIdRef.current = index.index_id;
      updateState({ selectedIndexId: index.index_id });
    } else {
      setUrlNotice('Requested index is no longer available. Showing your current index selection instead.');
      commitOwnedSearch({
        location,
        navigate,
        ownedKeys: OWNED_KEYS,
        updates: { index: null, view: null, video: null },
        mode: 'replace',
      });
    }

    pendingIndexIdRef.current = null;
  }, [isActive, indexes, location, navigate, setSelectedIndex, updateState, urlHydrated]);

  useEffect(() => {
    if (
      !isActive
      || !urlHydrated
      || !indexesLoaded
      || isLoading
      || isLoadingVideos
      || pendingViewRef.current === null
      || isApplyingPendingViewRef.current
    ) return;
    const requestedView = pendingViewRef.current;
    const urlGeneration = urlApplicationGenerationRef.current;
    const selectedIndexGeneration = selectedIndexGenerationRef.current;
    const ownsUrlApplication = () => (
      urlApplicationGenerationRef.current === urlGeneration
      && selectedIndexGenerationRef.current === selectedIndexGeneration
    );
    isApplyingPendingViewRef.current = true;

    const applyView = async () => {
      try {
        if (requestedView === 'list') {
          setSelectedVideoForDetail(null);
          updateState({ showAddVideoModal: false, showCreateModal: false });
          setShowExportModal(false);
          return;
        }

        if (requestedView === 'create') {
          updateState({ showCreateModal: true });
          setSelectedVideoForDetail(null);
          updateState({ showAddVideoModal: false });
          setShowExportModal(false);
          return;
        }

        if (selectedIndexRef.current?.is_default && ['add', 'process', 'export'].includes(requestedView)) {
          setUrlNotice(DEMO_INDEX_DISABLED_REASON);
          updateState({ showAddVideoModal: false, showCreateModal: false });
          setShowExportModal(false);
          return;
        }

        if (requestedView === 'add') {
          if (selectedIndexRef.current?.index_id === 'playground') {
            navigate('/playground?step=media', { replace: true });
            return;
          }
          setAddVideoMode(pendingIngestModeRef.current);
          setSelectedVideoForDetail(null);
          setShowExportModal(false);
          updateState({ showAddVideoModal: true, showCreateModal: false });
          return;
        }

        if (requestedView === 'process') {
          const indexId = selectedIndexRef.current?.index_id;
          const params = new URLSearchParams();
          if (indexId) params.set('index', indexId);
          const legacySearch = new URLSearchParams(location.search);
          const promptId = legacySearch.get('prompt');
          const runId = legacySearch.get('run');
          if (promptId) params.set('prompt', promptId);
          if (runId) params.set('run', runId);
          navigate(`/workflows${params.toString() ? `?${params.toString()}` : ''}`, { replace: true });
          return;
        }

        if (requestedView === 'export') {
          setSelectedVideoForDetail(null);
          updateState({ showAddVideoModal: false, showCreateModal: false });
          setShowExportModal(true);
          return;
        }

        if (requestedView === 'video') {
          const requestedVideoId = pendingVideoIdRef.current;
          if (!requestedVideoId) {
            setUrlNotice('No video id was provided for the requested detail view.');
            commitOwnedSearch({
              location,
              navigate,
              ownedKeys: OWNED_KEYS,
              updates: { view: 'list', video: null },
              mode: 'replace',
            });
            return;
          }
          if (acceptedVideoDeletionIdsRef.current.has(requestedVideoId)) {
            setUrlNotice('Requested video is being deleted and is no longer available.');
            commitOwnedSearch({
              location,
              navigate,
              ownedKeys: OWNED_KEYS,
              updates: { view: 'list', video: null },
              mode: 'replace',
            });
            return;
          }

          const requestedIndexId = selectedIndexRef.current?.index_id || null;
          if (!requestedIndexId) {
            throw new Error('No selected index is available for the requested video');
          }

          const indexVideo = videosIndexIdRef.current === requestedIndexId
            ? videosRef.current.find((item) => item.video_id === requestedVideoId)
            : undefined;
          const requestedRunId = new URLSearchParams(location.search).get('run');
          const runPage = requestedRunId ? runVideosByRunIdRef.current[requestedRunId] : undefined;
          const matchPage = requestedRunId ? workflowMatchVideosByRunIdRef.current[requestedRunId] : undefined;
          const localVideo = indexVideo
            || (runPage?.indexId === requestedIndexId ? runPage.videos.find((item) => item.video_id === requestedVideoId) : undefined)
            || (matchPage?.indexId === requestedIndexId ? matchPage.videos.find((item) => item.video_id === requestedVideoId) : undefined);
          if (localVideo) {
            setSelectedVideoForDetail(localVideo);
            updateState({ showAddVideoModal: false, showCreateModal: false });
            setShowExportModal(false);
            return;
          }

          try {
            let cursor = videosIndexIdRef.current === requestedIndexId
              ? videosNextCursor || undefined
              : undefined;
            let hasAnotherPage = videosIndexIdRef.current !== requestedIndexId || hasMoreVideos;
            let fetchedVideo: Video | null = null;
            const seenCursors = new Set<string>();

            while (hasAnotherPage && !fetchedVideo) {
              const page = requestedIndexId === 'playground'
                ? await apiService.getPlaygroundVideos(100, cursor)
                : await apiService.getVideosInIndex(requestedIndexId, 100, cursor);
              if (!ownsUrlApplication()) return;
              fetchedVideo = page.data.find((item) => item.video_id === requestedVideoId) || null;
              hasAnotherPage = page.pagination.has_more;
              const nextCursor = page.pagination.next_cursor || undefined;
              if (hasAnotherPage && (!nextCursor || seenCursors.has(nextCursor))) {
                throw new Error('Index video pagination did not advance');
              }
              if (nextCursor) seenCursors.add(nextCursor);
              cursor = nextCursor;
            }

            if (!fetchedVideo) {
              throw new Error('Requested video is not a member of this index');
            }
            if (!ownsUrlApplication()) {
              return;
            }
            if (
              acceptedVideoDeletionIdsRef.current.has(fetchedVideo.video_id)
            ) {
              throw new Error('Video deletion has already been accepted');
            }
            const durableGridVideo: Video = {
              ...fetchedVideo,
              ...projectIndexVideoPreviewFields(fetchedVideo),
            };
            videosIndexIdRef.current = requestedIndexId;
            publishVideoMutation([durableGridVideo.video_id], (prev) => (
              prev.some((item) => item.video_id === durableGridVideo.video_id)
                ? prev
                : [durableGridVideo, ...prev]
            ));
            setSelectedVideoForDetail(fetchedVideo);
            updateState({ showAddVideoModal: false, showCreateModal: false });
            setShowExportModal(false);
          } catch {
            if (!ownsUrlApplication()) {
              return;
            }
            setUrlNotice('Requested video is no longer available.');
            commitOwnedSearch({
              location,
              navigate,
              ownedKeys: OWNED_KEYS,
              updates: { view: 'list', video: null },
              mode: 'replace',
            });
          }
        }
      } finally {
        // A newer URL parser owns its own apply lifecycle. If only the index
        // selection changed, release this application so the canonical URL
        // sync can settle on the new selection instead of remaining wedged.
        if (urlApplicationGenerationRef.current !== urlGeneration) {
          return;
        }
        window.setTimeout(() => {
          if (urlApplicationGenerationRef.current !== urlGeneration) {
            return;
          }
          pendingViewRef.current = null;
          isApplyingPendingViewRef.current = false;
        }, 0);
      }
    };

    applyView();
  }, [
    isActive,
    indexesLoaded,
    hasMoreVideos,
    isLoading,
    isLoadingVideos,
    location,
    navigate,
    publishVideoMutation,
    updateState,
    urlHydrated,
    videosNextCursor,
  ]);

  const currentView: IndexView = selectedVideoForDetail
    ? 'video'
    : showAddVideoModal
      ? 'add'
      : showExportModal
        ? 'export'
        : showCreateModal
          ? 'create'
          : 'list';

  const shouldSubscribeToProcessingStream = useMemo(() => {
    // New automation/import events can be the first in-progress signal for an
    // otherwise idle index, so the stream cannot be gated by existing rows.
    return shouldSubscribeToIndexProcessingStream(
      isActiveAndVisible,
      selectedIndex?.index_id,
    );
  }, [
    isActiveAndVisible,
    selectedIndex?.index_id,
  ]);

  useEffect(() => {
    if (!isActive || !urlHydrated || isApplyingUrlRef.current || isApplyingPendingViewRef.current || pendingViewRef.current !== null || !!pendingIndexIdRef.current) return;
    if (selectedPromptRunId && !resultModeResolvedFromUrlRef.current) return;

    const mode = historyModeRef.current;
    historyModeRef.current = 'replace';
    commitOwnedSearch({
      location,
      navigate,
      ownedKeys: OWNED_KEYS,
      updates: {
        index: selectedIndex?.index_id || null,
        view: currentView,
        video: currentView === 'video' ? selectedVideoForDetail?.video_id || null : null,
        run: currentView === 'video'
          ? (deepLinkRunId || selectedPromptRunId || null)
          : (selectedPromptRunId || null),
        result: getIndexResultUrlParam(
          selectedPromptRunId,
          resultMode,
          resultModeResolvedFromUrlRef.current,
        ),
        t: currentView === 'video' ? deepLinkTime : null,
        field: currentView === 'video' ? deepLinkFieldKey : null,
        segment: currentView === 'video' ? resultLink?.segmentId : null,
        output: currentView === 'video' ? resultLink?.output : null,
        playback: currentView === 'video' ? resultLink?.playback : null,
        metadata: currentView === 'video' ? resultLink?.metadata : null,
        ingest: currentView === 'add' ? addVideoMode : null,
        vq: videoSearchQuery || null,
        vsort: videoSortBy,
        vdir: videoSortAsc ? 'asc' : 'desc',
        isort: sortBy,
        idir: sortAsc ? 'asc' : 'desc',
      },
      mode,
    });
  }, [
    isActive,
    addVideoMode,
    currentView,
    deepLinkFieldKey,
    resultLink,
    deepLinkRunId,
    deepLinkTime,
    isResultModeResolvedFromUrl,
    location,
    navigate,
    selectedPromptRunId,
    resultMode,
    selectedIndex?.index_id,
    selectedVideoForDetail?.video_id,
    sortAsc,
    sortBy,
    urlHydrated,
    videoSearchQuery,
    videoSortAsc,
    videoSortBy,
  ]);

  const loadPromptRuns = useCallback((
    indexId: string,
    options: PromptRunsLoadOptions = {},
  ): Promise<PromptRun[] | null> => {
    const stableIndexId = String(indexId || '').trim();
    const scopeId = getIndexPromptRunScopeId(stableIndexId, currentUserIdRef.current);
    if (!scopeId) return Promise.resolve(null);

    const selectionGeneration = selectedIndexGenerationRef.current;
    const existingRequest = promptRunsLoadInFlightRef.current.get(stableIndexId);
    if (existingRequest?.selectionGeneration === selectionGeneration) {
      return existingRequest.request;
    }

    const requestGeneration = ++promptRunsLoadGenerationRef.current;
    const mutationRevisionAtStart = promptRunMutationRevisionRef.current;
    const ownsRequest = () => (
      promptRunsLoadGenerationRef.current === requestGeneration
      && selectedIndexGenerationRef.current === selectionGeneration
      && selectedIndexRef.current?.index_id === stableIndexId
    );
    const preserveExistingOnError = Boolean(
      options.background
      || options.preserveExistingOnError
      || promptRunsIndexIdRef.current === stableIndexId
    );

    if (!options.background) {
      setIsLoadingPromptRuns(true);
    }
    setPromptRunsError(null);

    const request = (async (): Promise<PromptRun[] | null> => {
      try {
        const runs = await apiService.getAllPromptRunsByIndex(scopeId, 50);
        if (!ownsRequest()) return null;

        const sortedSnapshot = sortPromptRunsByExecutionDesc(runs || []);
        const currentRuns = promptRunsIndexIdRef.current === stableIndexId
          ? promptRunsRef.current
          : [];
        const mergedRuns = sortPromptRunsByExecutionDesc(
          mergeEntitySnapshotPreservingMutations({
            snapshot: sortedSnapshot,
            current: currentRuns,
            getId: (run) => run.run_id,
            mutationRevisionById: promptRunMutationRevisionByIdRef.current,
            requestStartRevision: mutationRevisionAtStart,
            mergeConcurrentMutation: (snapshotRun, currentRun) => ({
              ...snapshotRun,
              ...currentRun,
              run_context: currentRun.run_context || snapshotRun.run_context || {},
            }),
          }),
        );
        setPromptRuns(mergedRuns);
        setPromptRunsIndexId(stableIndexId);
        return mergedRuns;
      } catch (loadError) {
        if (!ownsRequest()) return null;
        console.error('Failed to load prompt runs for selected index:', loadError);
        if (!preserveExistingOnError) {
          setPromptRuns([]);
          setPromptRunsIndexId(stableIndexId);
        }
        setPromptRunsError('Failed to load workflow executions for this index.');
        return null;
      } finally {
        if (ownsRequest() && !options.background) {
          setIsLoadingPromptRuns(false);
        }
      }
    })();

    promptRunsLoadInFlightRef.current.set(stableIndexId, {
      selectionGeneration,
      request,
    });
    void request.then(
      () => {
        if (promptRunsLoadInFlightRef.current.get(stableIndexId)?.request === request) {
          promptRunsLoadInFlightRef.current.delete(stableIndexId);
        }
      },
      () => {
        if (promptRunsLoadInFlightRef.current.get(stableIndexId)?.request === request) {
          promptRunsLoadInFlightRef.current.delete(stableIndexId);
        }
      },
    );
    return request;
  }, [setPromptRuns, setPromptRunsIndexId]);

  useEffect(() => {
    const stableIndexId = selectedIndex?.index_id;
    if (!stableIndexId) {
      promptRunsLoadGenerationRef.current += 1;
      setPromptRuns([]);
      setPromptRunsIndexId(null);
      setIsLoadingPromptRuns(false);
      setPromptRunsError(null);
      setRunFailureManifestByRunId({});
      runFailureManifestRequestVersionRef.current = {};
      clearRunFailureManifestTimers();
      return;
    }

    if (promptRunsIndexIdRef.current === stableIndexId) {
      setIsLoadingPromptRuns(false);
      setPromptRunsError(null);
      return;
    }

    clearRunFailureManifestTimers();
    runVideosRequestVersionRef.current = {};
    setRunVideosByRunId({});
    void loadPromptRuns(stableIndexId);
  }, [
    clearRunFailureManifestTimers,
    loadPromptRuns,
    selectedIndex?.index_id,
    setPromptRuns,
    setPromptRunsIndexId,
  ]);

  useEffect(() => {
    const stableIndexId = selectedIndex?.index_id;
    if (!stableIndexId) {
      return;
    }
    if (!selectedPromptRunId) {
      return;
    }
    if (
      promptRunsIndexId !== stableIndexId
      || isLoadingPromptRuns
      || promptRunsError
      || selectedIndexRef.current?.index_id !== stableIndexId
      || (pendingIndexIdRef.current && pendingIndexIdRef.current !== stableIndexId)
    ) {
      return;
    }
    if (promptRuns.some((run) => run.run_id === selectedPromptRunId)) {
      return;
    }

    updateState({ selectedPromptRunId: null, resultMode: 'all' });
    if (currentView !== 'video') {
      setUrlNotice('Requested workflow execution was not found for this index. Switched to the combined index.');
    }
  }, [
    currentView,
    isLoadingPromptRuns,
    promptRuns,
    promptRunsIndexId,
    promptRunsError,
    selectedIndex?.index_id,
    selectedPromptRunId,
    updateState,
  ]);

  useEffect(() => {
    if (!selectedPromptRunId) {
      return;
    }
    const selectedRun = promptRuns.find((run) => run.run_id === selectedPromptRunId);
    if (!selectedRun || !isPromptRunTerminalStatus(selectedRun.status)) {
      return;
    }
    void refreshRunFailureManifest(selectedPromptRunId);
  }, [promptRuns, refreshRunFailureManifest, selectedPromptRunId]);

  const loadIndexes = useCallback(async (
    authoritativeIndexes?: Index[],
  ): Promise<Index[] | null> => {
    const requestToken: AsyncRequestOwnershipToken = {
      generation: ++indexLoadRequestGenerationRef.current,
      ownerKey: buildAsyncRequestOwnerKey('indexes'),
    };
    const ownsRequest = () => isCurrentAsyncRequest(
      indexLoadRequestGenerationRef.current,
      buildAsyncRequestOwnerKey('indexes'),
      requestToken,
    );

    try {
      setIsLoading(true);
      const data = authoritativeIndexes || await apiService.getIndexes();
      if (!ownsRequest()) {
        return null;
      }
      
      // All workspaces can browse their private Playground media collection.
      const playgroundIndex: Index = {
        index_id: 'playground',
        name: 'Playground',
        user_id: '', // Will be filled by backend with actual user_id
        created_at: new Date().toISOString(), // Use current date for sorting
      };
      
      const allIndexes = [playgroundIndex, ...data];
      const requestedSelectionId = (
        pendingIndexIdRef.current
        || selectedIndexRef.current?.index_id
      );
      const {
        usableIndexes,
        selectedIndex: nextSelection,
      } = deriveUsableIndexRefreshState(
        allIndexes,
        acceptedIndexDeletionIdsRef.current,
        requestedSelectionId,
      );
      const requestedSelectionWasRetained = Boolean(
        requestedSelectionId
        && nextSelection?.index_id === requestedSelectionId,
      );

      setIndexes(usableIndexes);
      setIndexesLoaded(true);
      setError((previousError) => previousError === INDEX_LOAD_ERROR ? null : previousError);
      
      if (nextSelection && requestedSelectionWasRetained) {
        setSelectedIndex(nextSelection);
        if (selectedIndexIdRef.current !== nextSelection.index_id) {
          selectedIndexIdRef.current = nextSelection.index_id;
          updateState({ selectedIndexId: nextSelection.index_id });
        }
      } else if (nextSelection) {
        setSelectedIndex(nextSelection);
        selectedIndexIdRef.current = nextSelection.index_id;
        updateState({
          selectedIndexId: nextSelection.index_id,
          selectedPromptRunId: null,
        });
      } else {
        setSelectedIndex(null);
        selectedIndexIdRef.current = null;
        updateState({
          selectedIndexId: null,
          selectedPromptRunId: null,
        });
      }
      return usableIndexes;
    } catch (err) {
      if (!ownsRequest()) {
        return null;
      }
      setError(INDEX_LOAD_ERROR);
      setErrorAction(null);
      console.error('Error loading indexes:', err);
      return null;
    } finally {
      if (ownsRequest()) {
        setIsLoading(false);
      }
    }
  }, [setIndexes, setSelectedIndex, updateState]);
  loadIndexesRef.current = loadIndexes;

  useEffect(() => {
    if (initialDataLoadStartedRef.current) {
      return;
    }
    initialDataLoadStartedRef.current = true;

    // Index membership is authoritative for this mount. Persisted identity is
    // used only to choose from the fresh catalog, never to render stale rows.
    void loadIndexes();

  }, [loadIndexes]);

  const VIDEOS_PAGE_SIZE = 50; // Server-side page size

  // Helper to normalize video payloads and apply realtime status projections.
  const processVideoDetails = useCallback((videoSummaries: Video[]): Video[] => {
    return videoSummaries.map(videoDetail => {
      const baseVideo: Video = {
        video_id: videoDetail.video_id,
        title: videoDetail.title,
        video_uri: videoDetail.video_uri,
        status: videoDetail.status,
        processing_status: videoDetail.processing_status || [],
        created_at: videoDetail.created_at,
        updated_at: videoDetail.updated_at,
        media_type: videoDetail.media_type,
        duration_seconds: videoDetail.duration_seconds,
        metadata_keys: videoDetail.metadata_keys,
        ...projectIndexVideoPreviewFields(videoDetail),
        marker: videoDetail.marker,
      };

      return applyRealtimeStateToVideo(
        baseVideo,
        processingRealtimeStore.getVideoState(videoDetail.video_id)
      );
    });
  }, []);

  const applyVideoRealtimeEventsSince = useCallback((
    videoPage: Video[],
    revision: number,
  ): Video[] => videoPage.map((video) => {
    const retainedEvents = videoRealtimeEventsByIdRef.current.get(video.video_id) || [];
    return retainedEvents
      .filter((entry) => entry.revision > revision)
      .reduce(
        (currentVideo, entry) => applyVideoAssetsReadyEvent(currentVideo, entry.event),
        video,
      );
  }), []);

  const pruneVideoRealtimeEventsThrough = useCallback((revision: number) => {
    videoRealtimeEventsByIdRef.current.forEach((entries, videoId) => {
      const retained = entries.filter((entry) => entry.revision > revision);
      if (retained.length > 0) {
        videoRealtimeEventsByIdRef.current.set(videoId, retained);
      } else {
        videoRealtimeEventsByIdRef.current.delete(videoId);
      }
    });
  }, []);

  const resolvePromptRun = useCallback((runId: string, indexId: string): PromptRun | null => {
    const localRun = promptRunsRef.current.find((run) => run.run_id === runId);
    if (!localRun) {
      return null;
    }
    const runIndexId = getPromptRunIndexId(localRun);
    return runIndexId && runIndexId !== getIndexPromptRunScopeId(indexId, currentUserIdRef.current) ? null : localRun;
  }, []);

  const loadRunVideoPage = useCallback(async (runId: string, indexId: string, reset: boolean = false) => {
    const stableRunId = String(runId || '').trim();
    const stableIndexId = String(indexId || '').trim();
    if (!stableRunId || !stableIndexId) {
      return;
    }

    const requestKey = `${stableIndexId}:${stableRunId}`;
    const existingBeforeRequest = runVideosByRunIdRef.current[stableRunId];
    if (!reset && existingBeforeRequest?.isLoading) {
      return;
    }
    const requestVersion = (runVideosRequestVersionRef.current[requestKey] || 0) + 1;
    runVideosRequestVersionRef.current[requestKey] = requestVersion;
    if (reset) {
      setRunVideosByRunId((current) => ({
        ...current,
        [stableRunId]: {
          runId: stableRunId,
          indexId: stableIndexId,
          videoIds: [],
          videos: [],
          loadedVideoIds: {},
          nextOffset: 0,
          hasMore: true,
          isLoading: true,
          error: null,
        },
      }));
    }

    const run = await resolvePromptRun(stableRunId, stableIndexId);
    if (
      runVideosRequestVersionRef.current[requestKey] !== requestVersion
      || selectedIndexRef.current?.index_id !== stableIndexId
    ) {
      return;
    }
    if (!run) {
      setRunVideosByRunId((current) => ({
        ...current,
        [stableRunId]: {
          runId: stableRunId,
          indexId: stableIndexId,
          videoIds: [],
          videos: [],
          loadedVideoIds: {},
          nextOffset: 0,
          hasMore: false,
          isLoading: false,
          error: 'Workflow execution not found.',
        },
      }));
      return;
    }

    const runVideoIds = sanitizeRunVideoIds(run);
    const existingState = reset ? undefined : runVideosByRunIdRef.current[stableRunId];
    const filteredExistingVideos = (existingState?.videos || []).filter((video) => runVideoIds.includes(video.video_id));
    const filteredExistingLoaded = Object.keys(existingState?.loadedVideoIds || {}).reduce<Record<string, true>>((acc, videoId) => {
      if (runVideoIds.includes(videoId)) {
        acc[videoId] = true;
      }
      return acc;
    }, {});

    const baseState: RunScopedVideosState = {
      runId: stableRunId,
      indexId: stableIndexId,
      videoIds: runVideoIds,
      videos: reset ? [] : filteredExistingVideos,
      loadedVideoIds: reset ? {} : filteredExistingLoaded,
      nextOffset: reset ? 0 : (existingState?.nextOffset || 0),
      hasMore: (reset ? 0 : (existingState?.nextOffset || 0)) < runVideoIds.length,
      isLoading: false,
      error: null,
    };

    if (!runVideoIds.length) {
      setRunVideosByRunId((current) => ({
        ...current,
        [stableRunId]: {
          ...baseState,
          hasMore: false,
          isLoading: false,
          error: null,
        },
      }));
      return;
    }

    if (!reset && existingState?.isLoading) {
      return;
    }

    const offset = reset ? 0 : (existingState?.nextOffset || 0);
    if (offset >= runVideoIds.length) {
      setRunVideosByRunId((current) => ({
        ...current,
        [stableRunId]: {
          ...baseState,
          hasMore: false,
          isLoading: false,
        },
      }));
      return;
    }

    const pageIds = runVideoIds.slice(offset, offset + RUN_VIDEO_PAGE_SIZE);
    if (!pageIds.length) {
      return;
    }

    setRunVideosByRunId((current) => ({
      ...current,
      [stableRunId]: {
        ...baseState,
        isLoading: true,
        error: null,
      },
    }));

    try {
      const chunks = chunkIds(pageIds, 100);
      const [summaryChunks, statusProjections] = await Promise.all([
        Promise.all(
          chunks.map((idsChunk) => apiService.getBatchVideoSummaries(idsChunk)),
        ),
        loadVideoStatusProjections(pageIds).catch((statusError) => {
          console.warn('Failed to hydrate extraction-run video tile status details:', statusError);
          return [] as VideoStatus[];
        }),
      ]);

      if (runVideosRequestVersionRef.current[requestKey] !== requestVersion) {
        return;
      }
      if (selectedIndexRef.current?.index_id !== stableIndexId) {
        return;
      }

      const flattened = summaryChunks.flat();
      const summaryById = new Map(flattened.map((video) => [video.video_id, video]));
      const orderedSummaries = pageIds
        .map((videoId) => summaryById.get(videoId))
        .filter((video): video is Video => !!video);
      const nextVideos = processVideoDetails(
        mergeVideoStatusProjections(orderedSummaries, statusProjections),
      );

      if (runVideosRequestVersionRef.current[requestKey] !== requestVersion) {
        return;
      }
      if (selectedIndexRef.current?.index_id !== stableIndexId) {
        return;
      }

      setRunVideosByRunId((current) => {
        const previous = current[stableRunId] || baseState;
        const mergedVideos = reset ? [] as Video[] : [...previous.videos];
        const loadedVideoIds = reset ? {} : { ...previous.loadedVideoIds };

        nextVideos.forEach((video) => {
          if (!loadedVideoIds[video.video_id]) {
            mergedVideos.push(video);
          }
          loadedVideoIds[video.video_id] = true;
        });

        const nextOffset = Math.min(offset + pageIds.length, runVideoIds.length);
        const hasMore = nextOffset < runVideoIds.length;

        return {
          ...current,
          [stableRunId]: {
            ...previous,
            runId: stableRunId,
            indexId: stableIndexId,
            videoIds: runVideoIds,
            videos: mergedVideos,
            loadedVideoIds,
            nextOffset,
            hasMore,
            isLoading: false,
            error: null,
          },
        };
      });
    } catch (loadError) {
      if (
        runVideosRequestVersionRef.current[requestKey] !== requestVersion
        || selectedIndexRef.current?.index_id !== stableIndexId
      ) {
        return;
      }
      console.error(`Failed loading run-scoped videos for run ${stableRunId}:`, loadError);
      setRunVideosByRunId((current) => {
        const previous = current[stableRunId] || baseState;
        return {
          ...current,
          [stableRunId]: {
            ...previous,
            videoIds: runVideoIds,
            isLoading: false,
            error: 'Failed to load media for this workflow execution.',
          },
        };
      });
    }
  }, [loadVideoStatusProjections, processVideoDetails, resolvePromptRun]);

  const loadMoreRunVideos = useCallback(async () => {
    if (!selectedIndex?.index_id || !selectedPromptRunId) {
      return;
    }
    const runState = runVideosByRunIdRef.current[selectedPromptRunId];
    if (!runState || runState.isLoading || !runState.hasMore) {
      return;
    }
    await loadRunVideoPage(selectedPromptRunId, selectedIndex.index_id, false);
  }, [loadRunVideoPage, selectedIndex?.index_id, selectedPromptRunId]);

  const loadWorkflowMatchVideoPage = useCallback(async (
    runId: string,
    indexId: string,
    reset: boolean,
  ) => {
    const stableRunId = String(runId || '').trim();
    const stableIndexId = String(indexId || '').trim();
    if (!stableRunId || !stableIndexId || selectedIndexRef.current?.index_id !== stableIndexId) {
      return;
    }

    const existing = reset ? undefined : workflowMatchVideosByRunIdRef.current[stableRunId];
    if (!reset && (!existing || existing.isLoading || !existing.hasMore)) return;
    const requestKey = `${stableIndexId}:${stableRunId}`;
    const requestVersion = (workflowMatchRequestVersionRef.current[requestKey] || 0) + 1;
    workflowMatchRequestVersionRef.current[requestKey] = requestVersion;
    const cursor = reset ? undefined : existing?.nextCursor || undefined;
    const baseState: WorkflowMatchVideosState = existing || {
      runId: stableRunId,
      indexId: stableIndexId,
      videos: [],
      loadedVideoIds: {},
      matchesByVideoId: {},
      nextCursor: null,
      hasMore: false,
      isLoading: false,
      error: null,
    };

    setWorkflowMatchVideosByRunId((current) => ({
      ...current,
      [stableRunId]: {
        ...(reset
          ? {
              ...baseState,
              videos: [],
              loadedVideoIds: {},
              matchesByVideoId: {},
            }
          : baseState),
        isLoading: true,
        error: null,
      },
    }));

    try {
      const page = await apiService.getPromptRunWorkflowResults(
        stableRunId,
        'video',
        RUN_VIDEO_PAGE_SIZE,
        cursor,
      );
      const videoItems = page.data.filter((item): item is PromptRunWorkflowVideoResult => (
        typeof item.video_id === 'string' && item.video_id.trim().length > 0
      ));
      const orderedVideoIds = Array.from(new Set(videoItems.map((item) => item.video_id.trim())));
      const summaryChunks = await Promise.all(
        chunkIds(orderedVideoIds, 100).map((ids) => apiService.getBatchVideoSummaries(ids)),
      );
      const summaryById = new Map(
        summaryChunks.flat().map((video) => [video.video_id, video]),
      );
      const orderedVideos = orderedVideoIds
        .map((videoId) => summaryById.get(videoId))
        .filter((video): video is Video => Boolean(video));
      const statuses = orderedVideos.length > 0
        ? await loadVideoStatusProjections(orderedVideos.map((video) => video.video_id)).catch(() => [] as VideoStatus[])
        : [];
      const hydratedVideos = processVideoDetails(
        mergeVideoStatusProjections(orderedVideos, statuses),
      );

      if (
        workflowMatchRequestVersionRef.current[requestKey] !== requestVersion
        || selectedIndexRef.current?.index_id !== stableIndexId
      ) return;

      setWorkflowMatchVideosByRunId((current) => {
        const previous = reset ? baseState : current[stableRunId] || baseState;
        const videos = reset ? [] as Video[] : [...previous.videos];
        const loadedVideoIds = reset ? {} as Record<string, true> : { ...previous.loadedVideoIds };
        const matchesByVideoId: WorkflowMatchVideosState['matchesByVideoId'] = reset
          ? {}
          : { ...previous.matchesByVideoId };
        videoItems.forEach((item) => {
          const videoId = item.video_id.trim();
          matchesByVideoId[videoId] = {
            video_matched: item.video_matched,
            matched_segment_ids: item.matched_segment_ids,
          };
        });
        hydratedVideos.forEach((video) => {
          if (!loadedVideoIds[video.video_id]) videos.push(video);
          loadedVideoIds[video.video_id] = true;
        });
        return {
          ...current,
          [stableRunId]: {
            runId: stableRunId,
            indexId: stableIndexId,
            videos,
            loadedVideoIds,
            matchesByVideoId,
            nextCursor: page.pagination.next_cursor || null,
            hasMore: Boolean(
              page.pagination.has_more
              && page.pagination.next_cursor
              && page.pagination.next_cursor !== cursor
            ),
            isLoading: false,
            error: null,
          },
        };
      });
    } catch (loadError) {
      if (
        workflowMatchRequestVersionRef.current[requestKey] !== requestVersion
        || selectedIndexRef.current?.index_id !== stableIndexId
      ) return;
      setWorkflowMatchVideosByRunId((current) => ({
        ...current,
        [stableRunId]: {
          ...(current[stableRunId] || baseState),
          isLoading: false,
          error: getApiErrorMessage(loadError, 'Failed to load selected workflow results.'),
        },
      }));
    }
  }, [loadVideoStatusProjections, processVideoDetails]);

  const loadMoreWorkflowMatchVideos = useCallback(async () => {
    if (!selectedIndex?.index_id || !selectedPromptRunId) return;
    await loadWorkflowMatchVideoPage(selectedPromptRunId, selectedIndex.index_id, false);
  }, [loadWorkflowMatchVideoPage, selectedIndex?.index_id, selectedPromptRunId]);

  useLayoutEffect(() => {
    if (!selectedIndex?.index_id || !selectedPromptRunId || resultMode === 'matches') {
      return;
    }
    if (promptRunsIndexId !== selectedIndex.index_id || isLoadingPromptRuns) {
      return;
    }
    const selectedRun = promptRuns.find((run) => run.run_id === selectedPromptRunId);
    if (!selectedRun) return;
    if (runVideosByRunIdRef.current[selectedPromptRunId]?.indexId === selectedIndex.index_id) {
      return;
    }
    void loadRunVideoPage(selectedPromptRunId, selectedIndex.index_id, true);
  }, [
    isLoadingPromptRuns,
    loadRunVideoPage,
    promptRuns,
    promptRunsIndexId,
    resultMode,
    selectedIndex?.index_id,
    selectedPromptRunId,
  ]);

  const loadVideos = useCallback(async (indexId: string, _forceRefresh: boolean = false) => {
    if (selectedIndexRef.current?.index_id !== indexId) {
      return;
    }
    if (videoFullLoadOwnerRef.current !== null) {
      return;
    }
    const fullLoadOwner = Symbol(indexId);
    videoFullLoadOwnerRef.current = fullLoadOwner;
    videoLoadMoreOwnerRef.current = null;
    const selectionGeneration = selectedIndexGenerationRef.current;
    const mutationRevisionAtStart = videoMutationRevisionRef.current;
    const requestToken: AsyncRequestOwnershipToken = {
      generation: ++videoLoadRequestGenerationRef.current,
      ownerKey: buildAsyncRequestOwnerKey(
        indexId,
        String(selectionGeneration),
      ),
    };
    const ownsRequest = () => isCurrentAsyncRequest(
      videoLoadRequestGenerationRef.current,
      buildAsyncRequestOwnerKey(
        selectedIndexRef.current?.index_id || null,
        String(selectedIndexGenerationRef.current),
      ),
      requestToken,
    );

    try {
      setIsLoadingMoreVideos(false);
      if (!ownsRequest()) {
        return;
      }
      setIsLoadingVideos(true);
      setIsLoadingMoreVideos(false);
      if (!_forceRefresh) {
        setVideoDatasetStatus('loading');
        videosIndexIdRef.current = null;
        setVideos([]);
        setVideosNextCursor(null);
        setHasMoreVideos(false);
      }
      let detailedVideos: VideoWithDetails[] = [];
      let nextCursor: string | undefined;
      let hasMore = false;

      if (indexId === 'playground') {
        // For playground, use paginated API directly
        const response = await apiService.getPlaygroundVideos(VIDEOS_PAGE_SIZE);
        if (!ownsRequest()) {
          return;
        }
        const data = response.data;
        hasMore = response.pagination.has_more;
        nextCursor = response.pagination.next_cursor;

        if (data.length > 0) {
          detailedVideos = data.map(v => ({
            ...v,
            updated_at: v.updated_at || v.created_at
          } as VideoWithDetails));

        }
      } else {
        // For regular indexes, use paginated API
        const response = await apiService.getVideosInIndex(
          indexId,
          VIDEOS_PAGE_SIZE,
          undefined,
          { view: 'gallery' },
        );
        if (!ownsRequest()) {
          return;
        }
        const data = response.data;
        hasMore = response.pagination.has_more;
        nextCursor = response.pagination.next_cursor;

        if (data.length > 0) {
          detailedVideos = data.map(v => ({
            ...v,
            updated_at: v.updated_at || v.created_at
          } as VideoWithDetails));

        }
      }

      // Keep preview payloads lightweight, then attach the dedicated status
      // projection before the page enters shared state.
      const hydratedVideos = await hydrateVideoStatusProjections(detailedVideos);
      const snapshotVideos = processVideoDetails(hydratedVideos);
      if (!ownsRequest()) {
        return;
      }

      const mergedVideos = applyVideoRealtimeEventsSince(
        mergeEntitySnapshotPreservingMutations({
          snapshot: snapshotVideos,
          current: videosRef.current,
          getId: (video) => video.video_id,
          mutationRevisionById: videoMutationRevisionByIdRef.current,
          requestStartRevision: mutationRevisionAtStart,
        }),
        mutationRevisionAtStart,
      );
      videosIndexIdRef.current = indexId;
      setVideos(mergedVideos);
      pruneVideoRealtimeEventsThrough(mutationRevisionAtStart);
      setVideosNextCursor(nextCursor || null);
      setHasMoreVideos(hasMore);
      setVideoDatasetStatus(mergedVideos.length > 0 ? 'success' : 'empty');

    } catch (err) {
      if (!ownsRequest()) {
        return;
      }
      setError('Failed to load videos');
      if (!_forceRefresh) {
        setVideosNextCursor(null);
        setHasMoreVideos(false);
        setVideoDatasetStatus('error');
      }
      console.error('Error loading videos:', err);
    } finally {
      if (videoFullLoadOwnerRef.current === fullLoadOwner) {
        videoFullLoadOwnerRef.current = null;
      }
      if (ownsRequest()) {
        setIsLoadingVideos(false);
      }
    }
  }, [
    applyVideoRealtimeEventsSince,
    hydrateVideoStatusProjections,
    pruneVideoRealtimeEventsThrough,
    processVideoDetails,
    setVideos,
  ]);

  useLayoutEffect(() => {
    const currentIndexId = selectedIndex?.index_id;
    if (!currentIndexId) {
      videoFullLoadOwnerRef.current = null;
      videoLoadMoreOwnerRef.current = null;
      videosIndexIdRef.current = null;
      setVideos([]);
      setIsLoadingVideos(false);
      setIsLoadingMoreVideos(false);
      setVideosNextCursor(null);
      setHasMoreVideos(false);
      setVideoDatasetStatus('idle');
      return;
    }

    void loadVideos(currentIndexId);
  }, [
    loadVideos,
    selectedIndex?.index_id,
    setVideos,
  ]);

  useEffect(() => {
    if (!selectedIndex?.index_id || isLoadingVideos || videoDatasetStatus === 'error') return;
    const nextStatus: DatasetStatus = videos.length > 0 ? 'success' : 'empty';
    if (videoDatasetStatus !== nextStatus) setVideoDatasetStatus(nextStatus);
  }, [isLoadingVideos, selectedIndex?.index_id, videoDatasetStatus, videos.length]);

  const refreshSelectedIndexFromServer = async () => {
    const refreshedIndexes = await loadIndexes();
    if (!refreshedIndexes) return;
    const currentSelectedIndexId = selectedIndexRef.current?.index_id;
    const refreshedSelectedIndex = currentSelectedIndexId
      ? refreshedIndexes.find((index) => index.index_id === currentSelectedIndexId)
      : null;
    const targetIndex = refreshedSelectedIndex || refreshedIndexes[0] || null;
    if (!targetIndex) return;

    const refreshRuns = loadPromptRuns(targetIndex.index_id, { preserveExistingOnError: true });

    if (isRunView && selectedPromptRunId && refreshedSelectedIndex) {
      const runId = selectedPromptRunId;
      await refreshRuns;
      if (
        selectedIndexRef.current?.index_id !== targetIndex.index_id
        || selectedPromptRunIdRef.current !== runId
      ) return;
      await loadRunVideoPage(runId, targetIndex.index_id, true);
      scheduleRunFailureManifestRefresh(runId, 0);
      return;
    }

    await Promise.all([
      refreshRuns,
      loadVideos(targetIndex.index_id, true),
    ]);
  };

  // Load more videos using cursor-based pagination
  const loadMoreVideos = async () => {
    const indexId = selectedIndexRef.current?.index_id;
    const cursor = videosNextCursor;
    if (
      !indexId
      || !cursor
      || videosIndexIdRef.current !== indexId
      || videoFullLoadOwnerRef.current !== null
      || videoLoadMoreOwnerRef.current !== null
    ) return;
    const loadMoreOwner = Symbol(indexId);
    videoLoadMoreOwnerRef.current = loadMoreOwner;
    const selectionGeneration = selectedIndexGenerationRef.current;
    const mutationRevisionAtStart = videoMutationRevisionRef.current;
    const requestToken: AsyncRequestOwnershipToken = {
      generation: ++videoLoadRequestGenerationRef.current,
      ownerKey: buildAsyncRequestOwnerKey(
        indexId,
        String(selectionGeneration),
      ),
    };
    const ownsRequest = () => isCurrentAsyncRequest(
      videoLoadRequestGenerationRef.current,
      buildAsyncRequestOwnerKey(
        selectedIndexRef.current?.index_id || null,
        String(selectedIndexGenerationRef.current),
      ),
      requestToken,
    );

    try {
      setIsLoadingMoreVideos(true);

      let response;
      if (indexId === 'playground') {
        response = await apiService.getPlaygroundVideos(VIDEOS_PAGE_SIZE, cursor);
      } else {
        response = await apiService.getVideosInIndex(
          indexId,
          VIDEOS_PAGE_SIZE,
          cursor,
          { view: 'gallery' },
        );
      }
      if (!ownsRequest()) {
        return;
      }

      const data = response.data;
      if (data.length > 0) {
        const detailedVideos: VideoWithDetails[] = data.map(v => ({
          ...v,
          updated_at: v.updated_at || v.created_at
        } as VideoWithDetails));

        const hydratedVideos = await hydrateVideoStatusProjections(detailedVideos);
        const newVideos = applyVideoRealtimeEventsSince(
          processVideoDetails(hydratedVideos),
          mutationRevisionAtStart,
        );
        if (!ownsRequest()) {
          return;
        }

        // Append to the mounted Index dataset atomically from latest state.
        setVideos(prevVideos => {
          if (!ownsRequest()) {
            return prevVideos;
          }
          return mergeEntityPagePreservingMutations({
            current: prevVideos,
            page: newVideos,
            getId: (video) => video.video_id,
            mutationRevisionById: videoMutationRevisionByIdRef.current,
            requestStartRevision: mutationRevisionAtStart,
          });
        });
        setVideoDatasetStatus('success');
        pruneVideoRealtimeEventsThrough(mutationRevisionAtStart);

      }

      setVideosNextCursor(response.pagination.next_cursor || null);
      setHasMoreVideos(response.pagination.has_more);

    } catch (err) {
      if (ownsRequest()) {
        setError('Failed to load more videos');
        console.error('Error loading more videos:', err);
      }
    } finally {
      if (videoLoadMoreOwnerRef.current === loadMoreOwner) {
        videoLoadMoreOwnerRef.current = null;
      }
      if (ownsRequest()) {
        setIsLoadingMoreVideos(false);
      }
    }
  };


  const handleCreateIndex = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mutationLockedFeature || !newIndexName.trim()) return;

    const request = { name: newIndexName.trim() };
    let ownedAttempt: AuthScopedMutationAttempt | null = null;
    try {
      setIsLoading(true);
      ownedAttempt = await beginMutationAttempt(
        'create-index',
        request,
        'index-create',
      );
      const newIndex = await apiService.createIndex(
        request,
        ownedAttempt.attempt.idempotencyKey,
      );
      clearMutationAttempt(ownedAttempt);
      indexLoadRequestGenerationRef.current += 1;
      setIndexes((currentIndexes) => {
        const existingIndex = currentIndexes.findIndex(
          (index) => index.index_id === newIndex.index_id,
        );
        if (existingIndex < 0) {
          return [...currentIndexes, newIndex];
        }
        const updatedIndexes = [...currentIndexes];
        updatedIndexes[existingIndex] = {
          ...updatedIndexes[existingIndex],
          ...newIndex,
        };
        return updatedIndexes;
      });
      setSelectedIndex(newIndex);
      selectedIndexIdRef.current = newIndex.index_id;
      
      // Set videos to empty array for the new index to prevent loading flicker
      setVideos([]);
      setIsLoadingVideos(false);
      setVideoDatasetStatus('empty');

      updateState({
        selectedIndexId: newIndex.index_id,
      });
      updateState({ 
        newIndexName: '',
        showCreateModal: false 
      });
    } catch (err) {
      if (ownedAttempt && !isRetryableStreamRequestError(err)) {
        clearMutationAttempt(ownedAttempt);
      }
      setError(getApiErrorMessage(err, 'Failed to create index.'));
      console.error('Error creating index:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const publishIndexDeletion = useCallback((
    targetIndex: Index,
    deletion: IndexDeletionResponse | null,
    initiatedAt: string,
    outcomeUncertain: boolean = false,
  ) => {
    const indexId = targetIndex.index_id;
    indexLoadRequestGenerationRef.current += 1;
    setIsLoading(false);
    acceptedIndexDeletionIdsRef.current.add(indexId);

    const currentSelectionId = (
      selectedIndexRef.current?.index_id
      || selectedIndexIdRef.current
    );
    const {
      usableIndexes,
      selectedIndex: nextSelection,
    } = deriveUsableIndexRefreshState(
      indexesRef.current,
      acceptedIndexDeletionIdsRef.current,
      currentSelectionId,
    );
    setIndexes(usableIndexes);

    setIndexTabState((previous) => {
      let nextPending = previous.pendingIndexDeletions || {};
      if (deletion) {
        nextPending = recordAcceptedIndexDeletion(
          nextPending,
          deletion,
          targetIndex.name,
          initiatedAt,
        );
      } else if (outcomeUncertain) {
        nextPending = recordUncertainIndexDeletion(
          nextPending,
          indexId,
          targetIndex.name,
          initiatedAt,
        );
      } else if (nextPending[indexId]) {
        nextPending = { ...nextPending };
        delete nextPending[indexId];
      }

      return {
        ...previous,
        pendingIndexDeletions: nextPending,
      };
    });

    const currentSelectionStillUsable = Boolean(
      currentSelectionId
      && usableIndexes.some((index) => index.index_id === currentSelectionId),
    );
    if (currentSelectionId && !currentSelectionStillUsable) {
      setSelectedIndex(nextSelection);
      selectedIndexIdRef.current = nextSelection?.index_id || null;
      setSelectedVideoForDetail(null);
      updateState({
        selectedIndexId: nextSelection?.index_id || null,
        selectedPromptRunId: null,
        showAddVideoModal: false,
      });
    }
  }, [setIndexTabState, setIndexes, setSelectedIndex, updateState]);

  const handleDeleteIndex = async (indexId: string) => {
    if (mutationLockedFeature) return;
    const targetIndex = indexes.find(index => index.index_id === indexId);
    if (!targetIndex || targetIndex.index_id === 'playground' || targetIndex.is_default) {
      return;
    }
    if (indexDeletionRequestsInFlightRef.current.has(indexId)) {
      return;
    }

    setOpenIndexMenuId(null);

    if (!window.confirm(`Are you sure you want to delete "${targetIndex.name}"? This action cannot be undone.`)) {
      return;
    }

    historyModeRef.current = 'push';
    const initiatedAt = new Date().toISOString();
    indexDeletionRequestsInFlightRef.current.add(indexId);
    try {
      const deletion = await apiService.deleteIndex(indexId);
      publishIndexDeletion(targetIndex, deletion, initiatedAt);
    } catch (err) {
      console.warn('Index deletion response was uncertain; reconciling:', err);
      try {
        const deletion = await apiService.getIndexDeletion(indexId);
        publishIndexDeletion(targetIndex, deletion, initiatedAt);
        setError(null);
        return;
      } catch (statusError) {
        console.warn('Unable to reconcile deletion from its tombstone:', statusError);
      }

      const authoritativeIndexes = await loadIndexes();
      if (authoritativeIndexes && !authoritativeIndexes.some(
        (index) => index.index_id === indexId,
      )) {
        publishIndexDeletion(targetIndex, null, initiatedAt);
        setError(null);
        return;
      }
      if (authoritativeIndexes) {
        setError('Failed to delete index');
        console.error('Index deletion was not accepted:', err);
        return;
      }

      // Both authoritative reads are unavailable. Hide the index and persist an
      // explicit uncertain poll instead of allowing a possibly tombstoned index
      // to remain operable or be restored from the in-memory cache.
      publishIndexDeletion(targetIndex, null, initiatedAt, true);
      setError(INDEX_DELETION_RECONCILING_MESSAGE);
    } finally {
      indexDeletionRequestsInFlightRef.current.delete(indexId);
    }
  };

  const handleDeleteVideo = async (videoId: string) => {
    if (mutationLockedFeature && selectedIndex?.index_id !== 'playground') return;
    if (!window.confirm('Are you sure you want to delete this video?')) {
      return;
    }

    try {
      const targetVideo = videos.find((video) => video.video_id === videoId);
      const deletion = await apiService.deleteVideo(videoId);
      acceptedVideoDeletionIdsRef.current.add(videoId);
      setIndexTabState((previous) => ({
        ...previous,
        pendingVideoDeletions: recordAcceptedVideoDeletion(
          previous.pendingVideoDeletions || {},
          deletion,
          targetVideo?.title || videoId,
          new Date().toISOString(),
        ),
      }));
      publishVideoMutation([videoId], prevVideos => {
        const updatedVideos = prevVideos.filter(video => video.video_id !== videoId);
        return updatedVideos;
      });

      setRunVideosByRunId((currentStates) => {
        let changed = false;
        const nextStates: Record<string, RunScopedVideosState> = {};

        Object.entries(currentStates).forEach(([runId, state]) => {
          if (!state.loadedVideoIds[videoId]) {
            nextStates[runId] = state;
            return;
          }

          changed = true;
          const nextLoaded = { ...state.loadedVideoIds };
          delete nextLoaded[videoId];
          const nextVideos = state.videos.filter((video) => video.video_id !== videoId);
          const nextVideoIds = state.videoIds.filter((id) => id !== videoId);
          const nextOffset = Math.min(state.nextOffset, nextVideoIds.length);

          nextStates[runId] = {
            ...state,
            videoIds: nextVideoIds,
            videos: nextVideos,
            loadedVideoIds: nextLoaded,
            nextOffset,
            hasMore: nextOffset < nextVideoIds.length,
          };
        });

        return changed ? nextStates : currentStates;
      });

      setWorkflowMatchVideosByRunId((currentStates) => {
        let changed = false;
        const nextStates: Record<string, WorkflowMatchVideosState> = {};
        Object.entries(currentStates).forEach(([runId, state]) => {
          if (!state.loadedVideoIds[videoId]) {
            nextStates[runId] = state;
            return;
          }
          changed = true;
          const loadedVideoIds = { ...state.loadedVideoIds };
          const matchesByVideoId = { ...state.matchesByVideoId };
          delete loadedVideoIds[videoId];
          delete matchesByVideoId[videoId];
          nextStates[runId] = {
            ...state,
            videos: state.videos.filter((video) => video.video_id !== videoId),
            loadedVideoIds,
            matchesByVideoId,
          };
        });
        return changed ? nextStates : currentStates;
      });
      
      await segmentCache.clearVideoCache(videoId);
      if (selectedVideoForDetail?.video_id === videoId) {
        setSelectedVideoForDetail(null);
      }
      
    } catch (err) {
      setError('Failed to delete video');
      console.error('Error deleting video:', err);
    }
  };

  const handleOpenExportModal = () => {
    if (!selectedIndex) return;

    historyModeRef.current = 'push';
    setShowExportModal(true);
  };

  const handleExportMetadata = async () => {
    if (!selectedIndex) return;
    if (!canUseExportLifecycle) return;
    if (exportSubmissionInFlightRef.current) return;

    const indexId = selectedIndex.index_id;
    const selectionGeneration = selectedIndexGenerationRef.current;
    const ownsOperation = () => ownsIndexSelection(indexId, selectionGeneration);
    const validRunIds = new Set(selectedIndexPromptRuns.map((run) => run.run_id));
    const exportScope = getIndexExportResultScope(
      selectedPromptRunId,
      isRunView,
      isMatchesView,
    );
    const selectedRunIds = exportScope.promptRunIds.filter((runId) => validRunIds.has(runId));
    if (
      (isRunView && exportScope.promptRunIds.length !== 1)
      || selectedRunIds.length !== exportScope.promptRunIds.length
    ) {
      setExportSuccess(null);
      setExportError('The active workflow execution changed. Review the result selector and try again.');
      setExportErrorAction(null);
      return;
    }

    cancelExportStatusPolling();
    const pollGeneration = exportStatusPollGenerationRef.current;
    let ownedAttempt: AuthScopedMutationAttempt | null = null;

    exportSubmissionInFlightRef.current = true;
    try {
      setIsExporting(true);
      setExportSuccess(null);
      setExportError(null);
      setExportErrorAction(null);

      const destination: {
        destination_connector_id?: string;
        destination_subpath?: string;
        result_scope: 'all' | 'matches';
      } = {
        ...(exportDestinationType === 'connector'
          ? {
              destination_connector_id: selectedExportConnectorId,
              destination_subpath: exportDestinationSubpath.trim() || undefined,
            }
          : {}),
        result_scope: exportScope.resultScope,
      };

      if (
        exportDestinationType === 'connector' &&
        (!selectedExportConnectorId || exportCapableConnectors.length === 0)
      ) {
        setExportError('Select an export connector for cloud delivery.');
        setExportErrorAction(null);
        setIsExporting(false);
        return;
      }

      const exportRequest = {
        index_id: indexId,
        prompt_run_ids: selectedRunIds,
        destination_type: exportDestinationType,
        destination_connector_id: destination?.destination_connector_id || null,
        destination_subpath: destination?.destination_subpath || null,
        result_scope: destination.result_scope,
      };
      ownedAttempt = await beginMutationAttempt(
        'create-metadata-export',
        exportRequest,
        `metadata-export-index:${indexId}`,
      );
      if (!ownsOperation()) {
        clearMutationAttempt(ownedAttempt);
        return;
      }

      let result: { export_id: string; status: string };
      if (selectedRunIds.length === 0) {
        // Export entire index
        result = await apiService.exportIndexMetadata(
          indexId,
          undefined,
          destination,
          ownedAttempt.attempt.idempotencyKey,
        );
      } else {
        // Export specific prompt runs
        result = await apiService.exportIndexMetadata(
          indexId,
          selectedRunIds,
          destination,
          ownedAttempt.attempt.idempotencyKey,
        );
      }
      clearMutationAttempt(ownedAttempt);

      if (
        exportStatusPollGenerationRef.current !== pollGeneration
        || !ownsOperation()
      ) {
        return;
      }

      setExportSuccess(
        exportDestinationType === 'connector'
          ? 'Export started successfully. Metadata will be delivered to the selected connector path.'
          : 'Export started successfully. A download link will be generated when ready.'
      );
      
      let pollInFlight = false;
      const pollExport = async (attempt: number) => {
        if (
          exportStatusPollGenerationRef.current !== pollGeneration
          || !ownsOperation()
        ) {
          return;
        }
        exportPollResumeRef.current = () => { void pollExport(attempt); };
        if (!canObserveRef.current || pollInFlight) return;
        pollInFlight = true;

        try {
          const exportStatus = await apiService.getExportStatus(result.export_id);
          if (
            exportStatusPollGenerationRef.current !== pollGeneration
            || !ownsOperation()
          ) {
            return;
          }
          if (!canObserveRef.current) return;
          exportPollResumeRef.current = null;

          if (exportStatus.status === 'completed') {
            const deliveryAuthority = getExportDeliveryAuthority(exportStatus);
            if (deliveryAuthority === 'authenticated_download') {
              await apiService.downloadExport(exportStatus.export_id);
              if (
                exportStatusPollGenerationRef.current !== pollGeneration
                || !ownsOperation()
              ) return;
              resetExportModalState();
              return;
            }

            if (deliveryAuthority === 'connector') {
              setExportSuccess(
                exportStatus.destination_uri
                  ? `Export delivered to ${exportStatus.destination_uri}`
                  : 'Export delivered to the selected cloud connector destination.'
              );
              try {
                const refreshedExports = await apiService.getExports(20);
                if (
                  exportStatusPollGenerationRef.current !== pollGeneration
                  || !ownsOperation()
                ) {
                  return;
                }
                const indexExports = refreshedExports.filter(exp =>
                  exp.export_type === 'index' && exp.target_id === indexId
                );
                setPreviousExports(indexExports);
                setExportHistoryError(null);
              } catch (refreshErr) {
                console.warn('Failed to refresh export history after completion:', refreshErr);
                setExportHistoryError(getApiErrorMessage(
                  refreshErr,
                  'Export completed, but export history could not be refreshed.',
                ));
              }
              return;
            }

            setExportError('Export completed but its delivery method is unavailable.');
            setExportErrorAction(null);
            return;
          } else if (exportStatus.status === 'failed') {
            setExportError(exportStatus.error_message || 'Export failed.');
            setExportErrorAction(null);
            setExportSuccess(null);
            return;
          }

          if (attempt >= EXPORT_STATUS_MAX_POLL_ATTEMPTS) {
            setExportSuccess('Export is still running. You can return later to download it from export history.');
            return;
          }

          const delay = EXPORT_STATUS_POLL_DELAYS_MS[
            Math.min(attempt, EXPORT_STATUS_POLL_DELAYS_MS.length - 1)
          ];
          exportPollResumeRef.current = () => { void pollExport(attempt + 1); };
          exportStatusPollTimerRef.current = window.setTimeout(() => {
            exportStatusPollTimerRef.current = null;
            void pollExport(attempt + 1);
          }, delay);
        } catch (err) {
          if (
            exportStatusPollGenerationRef.current !== pollGeneration
            || !ownsOperation()
          ) return;
          exportPollResumeRef.current = null;
          console.error('Error checking export status:', err);
          setExportApiError(
            err,
            'Failed to complete the metadata export download. Please try again from export history.',
          );
          setExportSuccess(null);
        } finally {
          pollInFlight = false;
        }
      };

      exportPollResumeRef.current = () => { void pollExport(1); };
      if (!canObserveRef.current) return;
      exportStatusPollTimerRef.current = window.setTimeout(() => {
        exportStatusPollTimerRef.current = null;
        void pollExport(1);
      }, EXPORT_STATUS_POLL_DELAYS_MS[0]);

    } catch (err: any) {
      if (ownedAttempt && !isRetryableStreamRequestError(err)) {
        clearMutationAttempt(ownedAttempt);
      }
      if (exportStatusPollGenerationRef.current !== pollGeneration) return;
      if (!ownsOperation()) return;
      setExportApiError(err, 'Failed to export metadata.');
      console.error('Error exporting metadata:', err);
    } finally {
      exportSubmissionInFlightRef.current = false;
      setIsExporting(false);
    }
  };

  const handleDownloadExport = async (exportJob: MetadataExport) => {
    try {
      setExportSuccess(null);
      setExportError(null);
      setExportErrorAction(null);
      const deliveryAuthority = getExportDeliveryAuthority(exportJob);
      if (deliveryAuthority === 'connector') {
        setExportError('This export was delivered to a cloud connector and does not have a download link.');
        return;
      }
      if (deliveryAuthority === 'authenticated_download') {
        await apiService.downloadExport(exportJob.export_id);
      } else {
        setExportError('Export is not completed yet.');
      }
    } catch (err) {
      console.error('Error downloading export:', err);
      setExportApiError(err, 'Failed to download export. Please try again.');
    }
  };

  const loadConnectors = useCallback(async (): Promise<Connector[]> => {
    if (!canUseConnectors) {
      connectorLoadRequestGenerationRef.current += 1;
      connectorsRef.current = [];
      setConnectors([]);
      setIsLoadingConnectors(false);
      return [];
    }
    const requestToken: AsyncRequestOwnershipToken = {
      generation: ++connectorLoadRequestGenerationRef.current,
      ownerKey: buildAsyncRequestOwnerKey('connectors'),
    };
    const ownsRequest = () => isCurrentAsyncRequest(
      connectorLoadRequestGenerationRef.current,
      buildAsyncRequestOwnerKey('connectors'),
      requestToken,
    );
    try {
      setIsLoadingConnectors(true);
      const data = await apiService.getConnectors();
      const activeConnectors = data.filter(c => c.status === 'active');
      if (!ownsRequest()) {
        // A newer global refresh owns publication, but this caller can still use
        // the immutable response it awaited for its own fenced workflow.
        return activeConnectors;
      }
      // Only show active connectors
      connectorsRef.current = activeConnectors;
      setConnectors(activeConnectors);
      return activeConnectors;
    } catch (err) {
      if (ownsRequest()) {
        console.error('Error loading connectors:', err);
      }
      return connectorsRef.current;
    } finally {
      if (ownsRequest()) {
        setIsLoadingConnectors(false);
      }
    }
  }, [canUseConnectors]);

  // Export configuration is a modal concern. Load only the references it
  // needs, independently, so a history outage cannot block the export flow or
  // trigger a duplicate extraction-run request already owned by Index state.
  useEffect(() => {
    const indexId = selectedIndex?.index_id;
    if (!showExportModal || !indexId || !canUseExportLifecycle) {
      setIsLoadingExports(false);
      setExportHistoryError(null);
      return;
    }

    let cancelled = false;
    setIsLoadingExports(true);
    setExportHistoryError(null);
    void loadConnectors();

    void apiService.getExports(20).then(
      (exports) => {
        if (cancelled || selectedIndexRef.current?.index_id !== indexId) return;
        setPreviousExports(exports.filter((exportJob) => (
          exportJob.export_type === 'index' && exportJob.target_id === indexId
        )));
      },
      (historyError) => {
        if (cancelled || selectedIndexRef.current?.index_id !== indexId) return;
        console.warn('Failed to load export history:', historyError);
        setPreviousExports([]);
        setExportHistoryError(getApiErrorMessage(
          historyError,
          'Export history is temporarily unavailable.',
        ));
      },
    ).finally(() => {
      if (!cancelled && selectedIndexRef.current?.index_id === indexId) {
        setIsLoadingExports(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    canUseExportLifecycle,
    loadConnectors,
    selectedIndex?.index_id,
    showExportModal,
  ]);

  const refreshIngestionHistory = useCallback(async (
    connectorSnapshot?: Connector[],
  ) => {
    const indexId = selectedIndexRef.current?.index_id;
    if (!indexId || !canUseImportLifecycle) return;

    const selectionGeneration = selectedIndexGenerationRef.current;
    const publicationGeneration = ++ingestionHistoryReadGenerationRef.current;
    const ownsPublication = () => (
      ingestionHistoryReadGenerationRef.current === publicationGeneration
      && selectedIndexGenerationRef.current === selectionGeneration
      && selectedIndexRef.current?.index_id === indexId
    );

    try {
      setIsRefreshingIngestionHistory(true);
      let request = ingestionHistoryReadInFlightRef.current;
      if (!request) {
        request = apiService.getImportJobs();
        ingestionHistoryReadInFlightRef.current = request;
        void request.then(
          () => {
            if (ingestionHistoryReadInFlightRef.current === request) {
              ingestionHistoryReadInFlightRef.current = null;
            }
          },
          () => {
            if (ingestionHistoryReadInFlightRef.current === request) {
              ingestionHistoryReadInFlightRef.current = null;
            }
          },
        );
      }
      const jobs = await request;
      if (!ownsPublication()) return;
      const connectorNameById = new Map(
        (connectorSnapshot || connectorsRef.current)
          .map(connector => [connector.connector_id, connector.name] as const),
      );
      const importEntries = jobs
        .filter(job => job.target_index_id === indexId)
        .map(job => projectImportIngestionHistoryItem(
          job as unknown as Record<string, unknown>,
          {
            indexId,
            connectorName: connectorNameById.get(job.connector_id || ''),
          },
        ))
        .filter((entry): entry is IngestionHistoryItem => entry !== null);

      upsertIngestionHistoryEntries(importEntries);
    } catch (historyErr) {
      if (ownsPublication()) {
        console.error('Failed to refresh ingestion history:', historyErr);
      }
    } finally {
      if (ingestionHistoryReadGenerationRef.current === publicationGeneration) {
        setIsRefreshingIngestionHistory(false);
      }
    }
  }, [
    canUseImportLifecycle,
    upsertIngestionHistoryEntries,
  ]);

  // Hydrate ingestion history once when its view opens. The shared processing
  // stream applies subsequent import-job events without a competing poller.
  useEffect(() => {
    const ingestionIndexId = selectedIndex?.index_id;
    if (
      !showAddVideoModal
      || !ingestionIndexId
      || addVideoMode !== 'import'
      || !canUseImportLifecycle
    ) {
      return;
    }

    let cancelled = false;
    void loadConnectors().then((latestConnectors) => {
      if (!cancelled) {
        void refreshIngestionHistory(latestConnectors);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    addVideoMode,
    canUseImportLifecycle,
    loadConnectors,
    refreshIngestionHistory,
    selectedIndex?.index_id,
    showAddVideoModal,
  ]);

  const handleCloudImport = async () => {
    if (!selectedConnectorId || !selectedIndex) return;
    if (!canUseImportLifecycle) return;
    if (importCreationInFlightRef.current) return;

    const targetIndexId = selectedIndex.index_id;
    const selectionGeneration = selectedIndexGenerationRef.current;
    const ownsOperation = () => ownsIndexSelection(targetIndexId, selectionGeneration);
    const request = {
      connector_id: selectedConnectorId,
      index_id: targetIndexId,
      source_prefix: importSourcePrefix,
      file_pattern: importFilePattern,
      recursive: importRecursive,
      import_mode_override: importModeOverride || undefined,
    };
    const connectorName = connectors.find(
      connector => connector.connector_id === request.connector_id,
    )?.name;
    let ownedAttempt: AuthScopedMutationAttempt | null = null;

    importCreationInFlightRef.current = true;
    try {
      setIsCreatingImportJob(true);
      setError(null);

      ownedAttempt = await beginMutationAttempt(
        'create-import-job',
        request,
        'import-job-create',
      );
      if (!ownsOperation()) {
        clearMutationAttempt(ownedAttempt);
        return;
      }
      const importJob = await apiService.createImportJob(
        request,
        ownedAttempt.attempt.idempotencyKey,
      );
      clearMutationAttempt(ownedAttempt);

      const projectedHistory = projectImportIngestionHistoryItem(
        importJob as unknown as Record<string, unknown>,
        {
          indexId: targetIndexId,
          connectorName,
        },
      );
      if (projectedHistory) {
        upsertIngestionHistoryEntries([projectedHistory]);
      }

      if (!ownsOperation()) {
        return;
      }

      setAddVideoMode('import');
      setSelectedConnectorId(importJob.connector_id || '');
      setImportSourcePrefix(importJob.source_prefix || '');
      setImportFilePattern(importJob.file_pattern || '*');
      setImportRecursive(importJob.recursive);
      setImportModeOverride(importJob.import_mode_override || '');

    } catch (err: any) {
      if (ownedAttempt && !isRetryableStreamRequestError(err)) {
        clearMutationAttempt(ownedAttempt);
      }
      if (!ownsOperation()) {
        return;
      }
      setApiError(err, 'Failed to create import job');
      console.error('Error creating import job:', err);
    } finally {
      importCreationInFlightRef.current = false;
      setIsCreatingImportJob(false);
    }
  };

  // Playground remains available to existing deep links, outside the index selector.
  const filteredAndSortedIndexes = useMemo(() => {
    return sortIndexes(indexes.filter((index) => index.index_id !== 'playground'), sortBy, sortAsc);
  }, [indexes, sortBy, sortAsc]);

  const allSelectedIndexPromptRuns = useMemo(() => {
    if (!selectedIndex?.index_id || promptRunsIndexId !== selectedIndex.index_id) {
      return [] as PromptRun[];
    }
    return promptRuns;
  }, [promptRuns, promptRunsIndexId, selectedIndex?.index_id]);

  const selectedIndexPromptRuns = allSelectedIndexPromptRuns;

  const selectedPromptRun = useMemo(() => {
    if (!selectedPromptRunId) {
      return null;
    }
    return allSelectedIndexPromptRuns.find((run) => run.run_id === selectedPromptRunId) || null;
  }, [allSelectedIndexPromptRuns, selectedPromptRunId]);

  const selectedRunVideosState = selectedPromptRunId ? runVideosByRunId[selectedPromptRunId] : null;
  const isRunView = Boolean(selectedPromptRunId && selectedPromptRun);
  const selectedRunHasFilter = promptRunHasSelectionFilter(selectedPromptRun);
  const isMatchesView = isRunView && selectedRunHasFilter && resultMode === 'matches';
  const selectedMatchVideosState = selectedPromptRunId
    ? workflowMatchVideosByRunId[selectedPromptRunId]
    : null;

  useEffect(() => {
    if (
      !selectedPromptRun
      || resultModeResolvedFromUrlRef.current
    ) {
      return;
    }
    resultModeResolvedFromUrlRef.current = true;
    setIsResultModeResolvedFromUrl(true);
    updateState({
      resultMode: getDefaultIndexResultMode(selectedPromptRun),
    });
  }, [
    selectedPromptRun,
    selectedRunHasFilter,
    isResultModeResolvedFromUrl,
    updateState,
  ]);

  useEffect(() => {
    if (resultMode === 'matches' && selectedPromptRun && !selectedRunHasFilter) {
      updateState({ resultMode: 'all' });
    }
  }, [resultMode, selectedPromptRun, selectedRunHasFilter, updateState]);

  useLayoutEffect(() => {
    if (
      !isMatchesView
      || !selectedPromptRunId
      || !selectedIndex?.index_id
      || selectedPromptRun?.selection_summary?.status !== 'ready'
      || selectedMatchVideosState
    ) return;
    void loadWorkflowMatchVideoPage(selectedPromptRunId, selectedIndex.index_id, true);
  }, [
    isMatchesView,
    loadWorkflowMatchVideoPage,
    selectedIndex?.index_id,
    selectedMatchVideosState,
    selectedPromptRun?.selection_summary?.status,
    selectedPromptRunId,
  ]);

  useEffect(() => {
    if (
      !isMatchesView
      || !selectedPromptRunId
      || !selectedIndex?.index_id
      || !selectedVideoForDetail
      || !selectedMatchVideosState
      || selectedMatchVideosState.isLoading
      || Boolean(selectedMatchVideosState.error)
      || !selectedMatchVideosState.hasMore
      || Object.prototype.hasOwnProperty.call(
        selectedMatchVideosState.matchesByVideoId,
        selectedVideoForDetail.video_id,
      )
    ) {
      return;
    }
    void loadWorkflowMatchVideoPage(selectedPromptRunId, selectedIndex.index_id, false);
  }, [
    isMatchesView,
    loadWorkflowMatchVideoPage,
    selectedIndex?.index_id,
    selectedMatchVideosState,
    selectedPromptRunId,
    selectedVideoForDetail,
  ]);

  const activeVideosSource = useMemo(() => {
    if (!isRunView) {
      return videos;
    }
    if (isMatchesView) {
      return selectedMatchVideosState?.videos || [];
    }
    return selectedRunVideosState?.videos || [];
  }, [isMatchesView, isRunView, selectedMatchVideosState?.videos, selectedRunVideosState?.videos, videos]);
  const indexProgressVideoIdsKey = JSON.stringify(activeVideosSource.slice(0, 100).map((video) => video.video_id));

  useEffect(() => {
    if (!shouldSubscribeToProcessingStream) {
      return;
    }

    const selectedIndexIdForState = selectedIndex?.index_id || null;
    if (!selectedIndexIdForState) {
      return;
    }

    const unsubscribe = processingRealtimeStore.subscribe((event) => {
      const eventRunId = String(event.payload?.run_id || event.run_id || '').trim();

      if (
        selectedIndexIdForState &&
        event.event_type === 'run.progress'
      ) {
        const runIdFromEvent = eventRunId;
        const existingRun = (
          runIdFromEvent
          && promptRunsIndexIdRef.current === selectedIndexIdForState
        )
          ? promptRunsRef.current.find((run) => run.run_id === runIdFromEvent)
          : undefined;
        const belongsToCurrentIndex = (
          eventMatchesSelectedIndex(event, selectedIndexIdForState) || !!existingRun
        );
        const patchedRun = belongsToCurrentIndex
          ? toPromptRunFromProcessingEvent(event, existingRun)
          : null;
        const patchedRunIndexId = getPromptRunIndexId(patchedRun);

        if (
          patchedRun
          && (!patchedRunIndexId || patchedRunIndexId === getIndexPromptRunScopeId(selectedIndexIdForState, currentUserIdRef.current))
          && selectedIndexRef.current?.index_id === selectedIndexIdForState
        ) {
          publishPromptRunMutation(patchedRun, selectedIndexIdForState);
        }
      }

      if (
        eventRunId &&
        selectedPromptRunId === eventRunId &&
        isPromptRunTerminalStatus(
          promptRunsRef.current.find((run) => run.run_id === eventRunId)?.status,
        ) &&
        event.event_type === 'run.progress'
      ) {
        scheduleRunFailureManifestRefresh(eventRunId);
      }

      if (
        canUseImportLifecycle
        && event.event_type.startsWith('import_job.')
        && eventMatchesSelectedIndex(event, selectedIndexIdForState)
      ) {
        const payload = (event.payload || {}) as Record<string, unknown>;
        const projectedHistory = projectImportIngestionHistoryItem(payload, {
          indexId: selectedIndexIdForState,
          connectorName: connectorsRef.current.find(
            (connector) => connector.connector_id === payload.connector_id
          )?.name,
          observedAt: event.created_at,
          statusFallback: getImportHistoryStatusFallback(event.event_type),
        });
        if (projectedHistory) {
          upsertIngestionHistoryEntries([projectedHistory]);
        }
      }

      if (event.event_type === 'import_job.video_imported' && eventMatchesSelectedIndex(event, selectedIndexIdForState)) {
        const importedVideo = event.payload?.video;
        const importedVideoId = String(importedVideo?.video_id || event.video_id || '').trim();

        if (importedVideo && importedVideoId) {
          const eventTime = event.created_at || new Date().toISOString();
          const createdAt = String(importedVideo.created_at || eventTime);
          const previewFields = projectIndexVideoPreviewFields(importedVideo);
          const nextVideo = applyRealtimeStateToVideo(
            {
              video_id: importedVideoId,
              title: String(importedVideo.title || importedVideoId),
              video_uri: String(importedVideo.video_uri || ''),
              status: importedVideo.status || 'not_processed',
              processing_status: Array.isArray(importedVideo.processing_status)
                ? importedVideo.processing_status
                : [],
              created_at: createdAt,
              updated_at: String(importedVideo.updated_at || createdAt),
              media_type: importedVideo.media_type || 'video',
              duration_seconds: importedVideo.duration_seconds ?? null,
              ...previewFields,
            },
            processingRealtimeStore.getVideoState(importedVideoId)
          );

          publishVideoMutation([importedVideoId], (prevVideos) => {
            const existingIndex = prevVideos.findIndex((video) => video.video_id === importedVideoId);
            const nextVideos = existingIndex >= 0 ? [...prevVideos] : [nextVideo, ...prevVideos];

            if (existingIndex >= 0) {
              nextVideos[existingIndex] = {
                ...prevVideos[existingIndex],
                ...nextVideo,
                marker: prevVideos[existingIndex].marker,
              };
            }

            return nextVideos;
          });
        }

        // This event was fully projected above. Do not run the generic video
        // patch path a second time for the same tile.
        return;
      }

      if (!event.video_id) return;

      const applyRealtimePatch = (sourceVideo: Video): Video => {
        const realtimeState = processingRealtimeStore.getVideoState(sourceVideo.video_id);
        return applyRealtimeStateToVideo(
          applyVideoAssetsReadyEvent(sourceVideo, event),
          realtimeState,
        );
      };

      publishVideoMutation([event.video_id], (prevVideos) => {
        let changed = false;
        const nextVideos = prevVideos.map((video) => {
          if (video.video_id !== event.video_id) {
            return video;
          }
          const updatedVideo = applyRealtimePatch(video);
          if (updatedVideo !== video) {
            changed = true;
          }
          return updatedVideo;
        });

        if (!changed) {
          return prevVideos;
        }

        return nextVideos;
      }, event);

      setRunVideosByRunId((currentStates) => {
        let hasChanges = false;
        const nextStates: Record<string, RunScopedVideosState> = {};

        Object.entries(currentStates).forEach(([runId, state]) => {
          if (state.indexId !== selectedIndexIdForState) {
            nextStates[runId] = state;
            return;
          }

          let runChanged = false;
          const nextRunVideos = state.videos.map((video) => {
            if (video.video_id !== event.video_id) {
              return video;
            }
            const updatedVideo = applyRealtimePatch(video);
            if (updatedVideo !== video) {
              runChanged = true;
            }
            return updatedVideo;
          });

          if (runChanged) {
            hasChanges = true;
            nextStates[runId] = {
              ...state,
              videos: nextRunVideos,
            };
            return;
          }

          nextStates[runId] = state;
        });

        return hasChanges ? nextStates : currentStates;
      });

      setSelectedVideoForDetail((currentVideo) => {
        if (!currentVideo || currentVideo.video_id !== event.video_id) {
          return currentVideo;
        }
        return applyRealtimePatch(currentVideo);
      });
    }, {
      index_id: getIndexPromptRunScopeId(selectedIndexIdForState, currentUserIdRef.current) || undefined,
      video_ids: JSON.parse(indexProgressVideoIdsKey),
      run_ids: selectedPromptRunId ? [selectedPromptRunId] : [],
    });

    return () => {
      unsubscribe();
      clearRunFailureManifestTimers();
    };
  }, [
    clearRunFailureManifestTimers,
    canUseImportLifecycle,
    eventMatchesSelectedIndex,
    scheduleRunFailureManifestRefresh,
    selectedIndex?.index_id,
    indexProgressVideoIdsKey,
    selectedPromptRunId,
    selectedVideoForDetail?.video_id,
    setPromptRunsIndexId,
    publishVideoMutation,
    shouldSubscribeToProcessingStream,
    upsertIngestionHistoryEntries,
    publishPromptRunMutation,
  ]);

  const activeDatasetStatus: DatasetStatus = isRunView
    ? isMatchesView
      ? selectedPromptRun?.selection_summary?.status === 'failed'
        ? 'error'
        : selectedPromptRun?.selection_summary?.status !== 'ready'
          ? 'loading'
          : !selectedMatchVideosState || (selectedMatchVideosState.isLoading && selectedMatchVideosState.videos.length === 0)
            ? 'loading'
            : selectedMatchVideosState.error
              ? 'error'
              : selectedMatchVideosState.videos.length > 0
                ? 'success'
                : 'empty'
      : !selectedRunVideosState || (selectedRunVideosState.isLoading && selectedRunVideosState.videos.length === 0)
      ? 'loading'
      : selectedRunVideosState.error
        ? 'error'
        : selectedRunVideosState.videos.length > 0
          ? 'success'
          : 'empty'
    : videoDatasetStatus;
  const activeDatasetIsLoading = activeDatasetStatus === 'loading';
  const activeDatasetError = isRunView
    ? isMatchesView
      ? selectedPromptRun?.selection_summary?.status === 'failed'
        ? 'Workflow output selection failed.'
        : selectedMatchVideosState?.error || null
      : selectedRunVideosState?.error || null
    : videoDatasetStatus === 'error'
      ? 'Failed to load videos for this index.'
      : null;
  const activeDatasetHasMore = isRunView
    ? isMatchesView ? !!selectedMatchVideosState?.hasMore : !!selectedRunVideosState?.hasMore
    : hasMoreVideos;
  const activeDatasetIsLoadingMore = isRunView
    ? isMatchesView ? !!selectedMatchVideosState?.isLoading : !!selectedRunVideosState?.isLoading
    : isLoadingMoreVideos;

  const selectedRunFailureManifest = useMemo(
    () => (selectedPromptRunId ? runFailureManifestByRunId[selectedPromptRunId] || null : null),
    [runFailureManifestByRunId, selectedPromptRunId]
  );

  const selectedRunVideoFailureMap = useMemo(
    () => getManifestVideoFailureMap(selectedRunFailureManifest),
    [selectedRunFailureManifest]
  );

  // Filter and sort active video dataset (union or run-scoped)
  const filteredAndSortedVideos = useMemo(() => {
    const filtered = activeVideosSource.filter(video =>
      video.title.toLowerCase().includes(videoSearchQuery.toLowerCase())
    );
    
    return sortVideos(filtered, videoSortBy, videoSortAsc, (video) => {
      const run = selectVideoRun(video.video_id, allSelectedIndexPromptRuns, selectedPromptRunId);
      return getDisplayedVideoStatus(video, run?.run_id || selectedPromptRunId, run?.status);
    });
  }, [activeVideosSource, allSelectedIndexPromptRuns, selectedPromptRunId, videoSearchQuery, videoSortBy, videoSortAsc]);

  // Calculate zoom percentage based on tiles per row
  const getZoomPercentage = (tiles: number): number => {
    // Inverse proportional: fewer tiles = zoomed in (higher %), more tiles = zoomed out (lower %)
    const zoomMap: { [key: number]: number } = {
      2: 250,  // Max zoom in (fewest tiles)
      3: 200,
      4: 150,
      5: 125,
      6: 100,  // Baseline (100% zoom)
      7: 85,
      8: 70,
      9: 60,
      10: 50,
      11: 45,
      12: 40   // Max zoom out (most tiles)
    };
    return zoomMap[tiles] || 100;
  };

  // The backend cursor is the sole pagination authority. Filtering and sorting
  // apply to the complete loaded page set without a second client-side window.
  const currentVideos = filteredAndSortedVideos;
  const totalFilteredItems = filteredAndSortedVideos.length;
  const visibleItemsCount = currentVideos.length;

  useEffect(() => {
    const wasPageVisible = previousPageVisibilityRef.current;
    previousPageVisibilityRef.current = isPageVisible;
    const resumedFromBackground = isIndexPageVisibilityResume(wasPageVisible, isPageVisible);
    const wasActiveAndVisible = previousActiveVisibilityRef.current;
    previousActiveVisibilityRef.current = isActiveAndVisible;
    const resumedActivity = isIndexPageVisibilityResume(wasActiveAndVisible, isActiveAndVisible);
    const stableIndexId = selectedIndex?.index_id;
    if (!isActiveAndVisible || (!resumedFromBackground && !resumedActivity) || !stableIndexId) {
      return;
    }

    const selectedRun = selectedPromptRunId
      ? promptRunsRef.current.find((run) => run.run_id === selectedPromptRunId)
      : null;
    const stableRunId = selectedRun
      ? selectedPromptRunId
      : null;
    let cancelled = false;
    const selectionGeneration = selectedIndexGenerationRef.current;
    const getCurrentVideoPage = (): Video[] => (
      stableRunId
        ? (runVideosByRunIdRef.current[stableRunId]?.videos || [])
        : videosRef.current
    );

    if (resumedFromBackground || resumedActivity) {
      // A visibility resume is the single background reconciliation path for
      // the run catalog. Refresh even when known runs are terminal so a missed
      // creation event is recovered after the stream reconnects.
      void loadPromptRuns(stableIndexId, {
        background: true,
        preserveExistingOnError: true,
      }).then(() => {
        if (
          cancelled
          || !stableRunId
          || selectedIndexRef.current?.index_id !== stableIndexId
        ) return;
        void loadRunVideoPage(stableRunId, stableIndexId, true);
      });
    }

    // Initial page loading owns initial hydration. This path is exclusively a
    // false-to-true visibility resume reconciliation.
    const shouldReconcileVideoStatuses = getCurrentVideoPage()
      .some((video) => isVideoInProgress(video, stableRunId));

    const reconcileStatuses = async () => {
      const requestToken: AsyncRequestOwnershipToken = {
        generation: ++statusReconcileRequestRef.current,
        ownerKey: buildAsyncRequestOwnerKey(
          stableIndexId,
          String(selectionGeneration),
          stableRunId || 'union',
        ),
      };
      const ownsRequest = () => (
        !cancelled
        && isCurrentAsyncRequest(
          statusReconcileRequestRef.current,
          buildAsyncRequestOwnerKey(
            selectedIndexRef.current?.index_id || null,
            String(selectedIndexGenerationRef.current),
            stableRunId || 'union',
          ),
          requestToken,
        )
      );

      try {
        const videoIds = getCurrentVideoPage()
          .map((video) => video.video_id)
          .filter(Boolean);
        if (videoIds.length === 0) return;

        const statusProjections = await loadVideoStatusProjections(videoIds);

        if (!ownsRequest()) {
          return;
        }

        setSelectedVideoForDetail((currentVideo) => {
          if (!currentVideo) {
            return currentVideo;
          }
          const mergedVideo = mergeVideoStatusProjections(
            [currentVideo],
            statusProjections,
            { preserveTerminalStatus: true },
          )[0];
          return mergedVideo;
        });

        if (stableRunId) {
          setRunVideosByRunId((currentStates) => {
            if (!ownsRequest()) {
              return currentStates;
            }
            const currentState = currentStates[stableRunId];
            if (!currentState || currentState.indexId !== stableIndexId) {
              return currentStates;
            }
            const nextVideos = mergeVideoStatusProjections(
              currentState.videos,
              statusProjections,
              { preserveTerminalStatus: true },
            );
            if (nextVideos === currentState.videos) {
              return currentStates;
            }
            return {
              ...currentStates,
              [stableRunId]: {
                ...currentState,
                videos: nextVideos,
              },
            };
          });
          return;
        }

        publishVideoMutation(videoIds, (prevVideos) => {
          if (!ownsRequest()) {
            return prevVideos;
          }
          const nextVideos = mergeVideoStatusProjections(
            prevVideos,
            statusProjections,
            { preserveTerminalStatus: true },
          );
          if (nextVideos === prevVideos) {
            return prevVideos;
          }

          return nextVideos;
        });
      } catch (statusError) {
        if (ownsRequest()) {
          console.warn('Failed to reconcile index video statuses:', statusError);
        }
      }
    };

    const timer = shouldReconcileVideoStatuses
      ? window.setTimeout(() => {
          void reconcileStatuses();
        }, STATUS_RECONCILE_DEBOUNCE_MS)
      : null;
    return () => {
      cancelled = true;
      if (timer !== null) {
        window.clearTimeout(timer);
      }
      statusReconcileRequestRef.current += 1;
    };
  }, [
    isActiveAndVisible,
    isPageVisible,
    loadPromptRuns,
    loadRunVideoPage,
    loadVideoStatusProjections,
    selectedIndex?.index_id,
    selectedPromptRunId,
    publishVideoMutation,
  ]);

  // Keep the video grid area as the dedicated scroll container.
  useEffect(() => {
    if (!isActive) return;
    const gridEl = gridContainerRef.current;
    const mainEl = indexMainRef.current;
    const videoControlsEl = videoControlsRef.current;
    if (!gridEl || !mainEl) return;

    let updateFrameId: number | null = null;
    const updateGridHeight = () => {
      if (updateFrameId !== null) {
        window.cancelAnimationFrame(updateFrameId);
      }

      updateFrameId = window.requestAnimationFrame(() => {
        updateFrameId = null;
        const mainRect = mainEl.getBoundingClientRect();
        const gridRect = gridEl.getBoundingClientRect();
        const availableHeight = Math.max(0, Math.floor(mainRect.bottom - gridRect.top));

        gridEl.style.height = `${availableHeight}px`;
        gridEl.style.maxHeight = `${availableHeight}px`;
        gridEl.style.flex = '1 1 auto';
        gridEl.style.overflowY = 'auto';
      });
    };

    updateGridHeight();

    const observer = new ResizeObserver(updateGridHeight);
    observer.observe(mainEl);
    if (videoControlsEl) {
      observer.observe(videoControlsEl);
    }
    window.addEventListener('resize', updateGridHeight);

    return () => {
      if (updateFrameId !== null) {
        window.cancelAnimationFrame(updateFrameId);
      }
      observer.disconnect();
      window.removeEventListener('resize', updateGridHeight);
    };
  }, [
    isActive,
    selectedIndex?.index_id,
    currentVideos.length,
    activeDatasetIsLoading,
    showTileDetails,
    tilesPerRow,
    activeVideosSource.length,
  ]);

  const toggleIndexSort = (field: 'name' | 'date') => {
    if (sortBy === field) {
      updateState({ sortAsc: !sortAsc });
    } else {
      updateState({ sortBy: field, sortAsc: true });
    }
  };

  const toggleVideoSort = (field: 'date' | 'name' | 'status') => {
    if (videoSortBy === field) {
      updateState({ videoSortAsc: !videoSortAsc });
    } else {
      updateState({ videoSortBy: field, videoSortAsc: true });
    }
  };

  const getSortIcon = (field: string, currentSort: string, currentOrder: string) => {
    if (field !== currentSort) {
      return <ArrowUpDown size={14} className="sort-icon-inactive" />;
    }
    return currentOrder === 'asc' ? 
      <ArrowUp size={14} className="sort-icon-active" /> : 
      <ArrowDown size={14} className="sort-icon-active" />;
  };

  const handleVideoTileClick = (video: Video) => {
    // Run membership may render while the index's initial page is still
    // loading. This user navigation supersedes that pending URL view.
    if (pendingViewRef.current !== null) {
      urlApplicationGenerationRef.current += 1;
      pendingViewRef.current = null;
      pendingVideoIdRef.current = null;
      isApplyingPendingViewRef.current = false;
    }
    historyModeRef.current = 'push';
    setResultLink(null);
    setDeepLinkRunId(selectedPromptRunId || null);
    setDeepLinkTime(null);
    setDeepLinkFieldKey(null);
    if (showAddVideoModal) {
      closeAddVideoSidebar();
    }
    // Always update the selected video, even if sidebar is already open
    // This allows switching between videos without closing the sidebar
    setSelectedVideoForDetail(video);
  };

  const handleCloseVideoDetail = () => {
    setResultLink(null);
    historyModeRef.current = 'push';
    setDeepLinkRunId(null);
    setDeepLinkTime(null);
    setDeepLinkFieldKey(null);
    setSelectedVideoForDetail(null);
  };

  const handleVideoMarkerChange = (videoId: string, marker: MarkerInfo) => {
    publishVideoMutation([videoId], prevVideos => {
      const nextVideos = prevVideos.map(v => (v.video_id === videoId ? { ...v, marker } : v));
      return nextVideos;
    });

    setRunVideosByRunId((currentStates) => {
      let changed = false;
      const nextStates: Record<string, RunScopedVideosState> = {};

      Object.entries(currentStates).forEach(([runId, state]) => {
        let runChanged = false;
        const nextVideos = state.videos.map((video) => {
          if (video.video_id !== videoId) {
            return video;
          }
          runChanged = true;
          return { ...video, marker };
        });

        if (runChanged) {
          changed = true;
          nextStates[runId] = {
            ...state,
            videos: nextVideos,
          };
        } else {
          nextStates[runId] = state;
        }
      });

      return changed ? nextStates : currentStates;
    });

    setWorkflowMatchVideosByRunId((currentStates) => {
      let changed = false;
      const nextStates: Record<string, WorkflowMatchVideosState> = {};
      Object.entries(currentStates).forEach(([runId, state]) => {
        let runChanged = false;
        const nextVideos = state.videos.map((video) => {
          if (video.video_id !== videoId) return video;
          runChanged = true;
          changed = true;
          return { ...video, marker };
        });
        nextStates[runId] = runChanged ? { ...state, videos: nextVideos } : state;
      });
      return changed ? nextStates : currentStates;
    });

    setSelectedVideoForDetail(prev => (prev?.video_id === videoId ? { ...prev, marker } : prev));

  };

  const activeCanonicalMediaCount = isMatchesView
    ? selectedPromptRun?.selection_summary?.selected_media_count ?? activeVideosSource.length
    : activeVideosSource.length;
  const headerVideoCountLabel = `Showing ${visibleItemsCount} of ${totalFilteredItems} media${
    totalFilteredItems !== activeVideosSource.length
      ? ` (${activeCanonicalMediaCount} total)`
      : isMatchesView && activeCanonicalMediaCount !== activeVideosSource.length
        ? ` (${activeCanonicalMediaCount} selected)`
        : ''
  }`;
  const selectIndexForBrowse = (indexId: string) => {
    const nextIndex = indexes.find((index) => index.index_id === indexId);
    if (!nextIndex) return;
    historyModeRef.current = 'push';
    setSelectedIndex(nextIndex);
    handleCloseVideoDetail();
    setOpenIndexMenuId(null);
    updateState({ selectedIndexId: indexId, selectedPromptRunId: null, resultMode: 'all' });
    setError(null);
    setUrlNotice(null);
  };
  const selectRunForBrowse = (run: PromptRun) => {
    historyModeRef.current = 'push';
    resultModeResolvedFromUrlRef.current = true;
    setIsResultModeResolvedFromUrl(true);
    handleCloseVideoDetail();
    updateState({
      selectedPromptRunId: run.run_id,
      resultMode: getDefaultIndexResultMode(run),
    });
    setUrlNotice(null);
  };
  const isDemoIndex = Boolean(selectedIndex?.is_default);
  const demoIndexDisabledReason = isDemoIndex
    ? DEMO_INDEX_DISABLED_REASON
    : undefined;
  const isAddVideoSidebarOpen = showAddVideoModal && addVideoMode === 'import' && !mutationLockedFeature && !isDemoIndex;
  const selectedIndexIsEmpty = videos.length === 0
    && (selectedIndex?.video_count === 0 || videoDatasetStatus === 'empty');
  const selectedIndexCreatedAt = selectedIndex?.created_at ? new Date(selectedIndex.created_at) : null;
  const headerIndexCreatedLabel = selectedIndexCreatedAt && !Number.isNaN(selectedIndexCreatedAt.getTime())
    ? `Index Created ${selectedIndexCreatedAt.toLocaleDateString()}`
    : 'Index Created Date Unavailable';
  const selectedPromptRunLabel = selectedPromptRun?.prompt_name || selectedPromptRun?.prompt_id || selectedPromptRun?.run_id;
  const headerViewModeLabel = isRunView
    ? `${isMatchesView ? 'Workflow Matches' : 'Workflow Output'}${selectedPromptRunLabel ? `: ${selectedPromptRunLabel}` : ''}`
    : 'Full view';
  return (
    <div
      className={`index-tab ${selectedVideoForDetail || isAddVideoSidebarOpen ? 'sidebar-open' : ''}`}
      style={{
        marginRight: selectedVideoForDetail
          ? `${sidebarWidth}px`
          : isAddVideoSidebarOpen
            ? `${addVideoSidebarWidth}px`
            : '0'
      }}
    >
      <div className="index-content">
        {/* Sidebar with indexes */}
        <div className="index-sidebar">
          <div className="index-sidebar-header">
            <div className="sidebar-title-row">
              <div className="sidebar-title-content">
                <h3>Indexes</h3>
                <span className="index-count-badge" aria-label={`${filteredAndSortedIndexes.length} indexes`}>
                  {filteredAndSortedIndexes.length} total
                </span>
              </div>
              <FeatureAction feature="workspace_mutation"><button
                type="button"
                className="sidebar-create-button"
                onClick={() => {
                  historyModeRef.current = 'push';
                  updateState({ showCreateModal: true });
                }}
                disabled={isLoading}
                title="Create new index"
              >
                <Plus size={16} />
              </button></FeatureAction>
            </div>
            <div className="index-sort-controls" aria-label="Sort indexes">
              <button
                type="button"
                className={`index-sort-button ${sortBy === 'name' ? 'active' : ''}`}
                onClick={() => toggleIndexSort('name')}
                title={`Sort by name ${sortBy === 'name' ? (sortAsc ? '(A-Z)' : '(Z-A)') : ''}`}
                aria-pressed={sortBy === 'name'}
              >
                <span>Name</span>
                {getSortIcon('name', sortBy, sortAsc ? 'asc' : 'desc')}
              </button>
              <button
                type="button"
                className={`index-sort-button ${sortBy === 'date' ? 'active' : ''}`}
                onClick={() => toggleIndexSort('date')}
                title={`Sort by date ${sortBy === 'date' ? (sortAsc ? '(oldest first)' : '(newest first)') : ''}`}
                aria-pressed={sortBy === 'date'}
              >
                <span>Date</span>
                {getSortIcon('date', sortBy, sortAsc ? 'asc' : 'desc')}
              </button>
            </div>
          </div>

          <div className="index-list">
            {Object.entries(pendingIndexDeletions).map(([indexId, deletion]) => (
              <div
                key={`deleting-${indexId}`}
                className="index-deletion-progress"
                role="status"
                aria-live="polite"
              >
                <RefreshCw size={14} className="index-spinning" />
                <span>
                  Deleting {deletion.index_name} — {deletion.status}
                </span>
              </div>
            ))}
            {filteredAndSortedIndexes.map((index) => {
              const isSelectedIndex = selectedIndex?.index_id === index.index_id;
              const canDeleteIndex = index.index_id !== 'playground' && !index.is_default;
              const isIndexMenuOpen = openIndexMenuId === index.index_id;

              return (
                <div key={index.index_id} className="index-item-group">
                  <div
                    className={`index-item ${isSelectedIndex && !selectedPromptRunId ? 'active' : ''} ${isSelectedIndex ? 'selected-parent' : ''} ${isIndexMenuOpen ? 'menu-open' : ''} ${index.index_id === 'playground' ? 'playground-item' : ''}`}
                  >
                    <button
                      type="button"
                      className="index-info"
                      onClick={() => selectIndexForBrowse(index.index_id)}
                      aria-label={`${index.name}: combined results`}
                      aria-pressed={isSelectedIndex && !selectedPromptRunId}
                    >
                      <span className="index-details">
                        <span className="index-name" title={index.name}>
                          {index.name}
                        </span>
                        <span className="index-date">
                          {index.index_id === 'playground' ? 'Your Experiments' :
                           index.is_default ? 'Demo' :
                           new Date(index.created_at).toLocaleDateString()}
                        </span>
                      </span>
                    </button>
                    {canDeleteIndex && (
                      <div
                        className={`index-actions ${isIndexMenuOpen ? 'menu-open' : ''}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          className="index-actions-trigger"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenIndexMenuId(prev => prev === index.index_id ? null : index.index_id);
                          }}
                          aria-haspopup="menu"
                          aria-expanded={isIndexMenuOpen}
                          aria-label={`Open actions for ${index.name}`}
                          title="Index actions"
                        >
                          <MoreHorizontal size={15} />
                        </button>

                        {isIndexMenuOpen && (
                          <div className="index-actions-menu" role="menu" aria-label={`Actions for ${index.name}`}>
                            <button
                              type="button"
                              className="index-action-item delete"
                              role="menuitem"
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleDeleteIndex(index.index_id);
                              }}
                            >
                              <Trash2 size={14} />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Main content */}
        <div className="index-main" ref={indexMainRef}>
          {selectedIndex ? (
            <>
              <div className="main-header">
                <div className="header-info">
                  <div className="index-title-chip" title={selectedIndex.name}>
                    <h3>{selectedIndex.name}</h3>
                  </div>
                  <div className="index-main-meta">
                    <span className="index-meta-chip index-created-date" title={headerIndexCreatedLabel}>
                      {headerIndexCreatedLabel}
                    </span>
                    <span className="index-meta-chip video-count" title={headerVideoCountLabel}>
                      {headerVideoCountLabel}
                    </span>
                    <span
                      className={`index-meta-chip index-view-mode ${isRunView ? 'run-view' : 'union-view'}`}
                      title={headerViewModeLabel}
                    >
                      {headerViewModeLabel}
                    </span>
                  </div>
                </div>
                <div className="index-header-actions-controls">
                  <div className="index-result-view-controls">
                    {isRunView && selectedRunHasFilter && (
                      <div className="index-result-mode-switch" aria-label="Workflow result mode">
                        <button
                          type="button"
                          className={resultMode === 'matches' ? 'active' : ''}
                          onClick={() => {
                            historyModeRef.current = 'push';
                            updateState({ resultMode: 'matches' });
                          }}
                          disabled={selectedPromptRun?.selection_summary?.status !== 'ready'}
                          title={selectedPromptRun?.selection_summary?.status === 'ready'
                            ? 'Show only selected workflow results'
                            : 'Matches are available after output selection completes'}
                        >
                          Matches ({selectedPromptRun?.selection_summary?.selected_media_count ?? 0})
                        </button>
                        <button
                          type="button"
                          className={resultMode === 'all' ? 'active' : ''}
                          onClick={() => {
                            historyModeRef.current = 'push';
                            updateState({ resultMode: 'all' });
                          }}
                        >
                          All extracted
                        </button>
                      </div>
                    )}
                  </div>
                  <button
                    className="refresh-button"
                    onClick={() => {
                      if (isMatchesView && selectedPromptRunId) {
                        void loadWorkflowMatchVideoPage(selectedPromptRunId, selectedIndex.index_id, true);
                      } else {
                        void refreshSelectedIndexFromServer();
                      }
                    }}
                    disabled={isLoading || activeDatasetIsLoading}
                    title={isMatchesView ? 'Refresh workflow matches' : isRunView ? 'Refresh workflow output' : 'Refresh current index from server'}
                  >
                    <RefreshCw size={16} className={(isLoading || activeDatasetIsLoading) ? 'index-spinning' : ''} />
                  </button>
                  {selectedIndex.index_id !== 'playground' && (
                    <>
                      {hasFeature('automation_integrations') && (
                        <IndexAutomationControl
                          indexId={selectedIndex.index_id}
                          runs={allSelectedIndexPromptRuns}
                          onSelectIndex={selectIndexForBrowse}
                          disabled={isDemoIndex}
                          disabledReason={demoIndexDisabledReason}
                        />
                      )}
                      <span className="index-disabled-action" title={demoIndexDisabledReason}>
                      <FeatureAction feature={isDemoIndex ? null : 'workspace_mutation'}><button
                        className="add-video-button"
                        onClick={openAddVideoSidebar}
                        disabled={isLoading || isDemoIndex}
                        title={demoIndexDisabledReason}
                      >
                        <Upload size={16} />
                        Add Video
                      </button></FeatureAction>
                      </span>
                      <span className="index-disabled-action" title={demoIndexDisabledReason}>
                      <FeatureAction feature={isDemoIndex ? null : 'import_lifecycle'}><button
                        className="add-video-button"
                        onClick={() => {
                          historyModeRef.current = 'push';
                          setSelectedVideoForDetail(null);
                          setAddVideoMode('import');
                          updateState({ showAddVideoModal: true });
                        }}
                        disabled={isLoading || isDemoIndex}
                        title={demoIndexDisabledReason}
                      ><Cloud size={16} />Import</button></FeatureAction>
                      </span>
                      <span className="index-disabled-action" title={demoIndexDisabledReason}>
                      <FeatureAction feature={isDemoIndex || selectedIndexIsEmpty ? null : 'workspace_mutation'}><button
                        type="button"
                        className="process-all-button"
                        onClick={() => {
                          const target = getExecuteLocation(selectedIndex.index_id);
                          navigate(target, { state: target.state });
                        }}
                        disabled={isLoading || isLoadingVideos || isDemoIndex || selectedIndexIsEmpty}
                        title={demoIndexDisabledReason}
                      >
                        Process index
                        <ArrowRight size={16} aria-hidden="true" />
                      </button></FeatureAction>
                      </span>
                      <span className="index-disabled-action" title={demoIndexDisabledReason}>
                      <button
                        className={`export-button ${exportLockedFeature ? 'locked-action' : ''}`}
                        onClick={handleOpenExportModal}
                        disabled={isLoading || isDemoIndex || (!exportLockedFeature && videos.length === 0)}
                        title={demoIndexDisabledReason || (exportLockedFeature ? exportLockedFeature.reason : 'Export metadata from this index')}
                      >
                        {exportLockedFeature ? <Lock size={16} /> : <Download size={16} />}
                        Export Metadata
                      </button>
                      </span>
                    </>
                  )}
                </div>
              </div>

              <div className="index-execution-tabs" role="tablist" aria-label="Index executions"
                onKeyDown={(event) => {
                  if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
                  const tabs = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]'));
                  const current = tabs.indexOf(event.target as HTMLButtonElement);
                  if (current < 0) return;
                  event.preventDefault();
                  const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1
                    : (current + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
                  tabs[next]?.focus();
                  tabs[next]?.click();
                }}>
                <button type="button" role="tab" aria-selected={!selectedPromptRunId}
                  tabIndex={!selectedPromptRunId ? 0 : -1}
                  className={!selectedPromptRunId ? 'active' : ''}
                  onClick={() => selectIndexForBrowse(selectedIndex.index_id)}>Full view</button>
                {selectedIndexPromptRuns.map((run) => (
                  <button key={run.run_id} type="button" role="tab"
                    aria-selected={selectedPromptRunId === run.run_id}
                    tabIndex={selectedPromptRunId === run.run_id ? 0 : -1}
                    className={selectedPromptRunId === run.run_id ? 'active' : ''}
                    onClick={() => selectRunForBrowse(run)}
                    title={`${run.prompt_name || 'Execution'} · ${new Date(run.executed_at).toLocaleString()} · ${run.status.replace(/_/g, ' ')}`}>
                    <span className={`index-run-status ${run.status}`} aria-hidden="true" />
                    <span>{run.prompt_name || run.prompt_id || 'Execution'}</span>
                    <small>{new Date(run.executed_at).toLocaleString()}</small>
                  </button>
                ))}
                {isLoadingPromptRuns && <span className="index-run-state" role="status">Loading executions…</span>}
                {promptRunsError && <span className="index-run-state error">{promptRunsError}
                  <button type="button" onClick={() => void loadPromptRuns(selectedIndex.index_id)}>Retry</button>
                </span>}
              </div>

              {activeVideosSource.length > 0 && (
                <div className="video-controls index-video-controls" ref={videoControlsRef}>
                  <div className="video-filter-controls">
                    <div className="search-container">
                      <Search size={16} className="search-icon" />
                      <input
                        type="text"
                        placeholder="Search videos..."
                        value={videoSearchQuery}
                        onChange={(e) => updateState({ videoSearchQuery: e.target.value })}
                        className="search-input"
                      />
                    </div>

                  </div>

                  <div className="sort-controls">
                    <div className="control-group">
                      <button
                        className={`control-button control-fixed ${videoSortBy === 'name' ? 'active' : ''}`}
                        onClick={() => toggleVideoSort('name')}
                        title={`Sort by title ${videoSortBy === 'name' ? (videoSortAsc ? '(A-Z)' : '(Z-A)') : ''}`}
                      >
                        <div className="control-content">
                          <span className="control-label">Title</span>
                          <span className="control-subtext">sort by title</span>
                        </div>
                        <span className="sort-indicator">{videoSortBy === 'name' ? (videoSortAsc ? '↑' : '↓') : ''}</span>
                      </button>
                      <button
                        className={`control-button control-fixed ${videoSortBy === 'date' ? 'active' : ''}`}
                        onClick={() => toggleVideoSort('date')}
                        title={`Sort by date ${videoSortBy === 'date' ? (videoSortAsc ? '(oldest first)' : '(newest first)') : ''}`}
                      >
                        <div className="control-content">
                          <span className="control-label">Date</span>
                          <span className="control-subtext">sort by date</span>
                        </div>
                        <span className="sort-indicator">{videoSortBy === 'date' ? (videoSortAsc ? '↑' : '↓') : ''}</span>
                      </button>
                      <button
                        className={`control-button control-fixed ${videoSortBy === 'status' ? 'active' : ''}`}
                        onClick={() => toggleVideoSort('status')}
                        title={`Sort by status ${videoSortBy === 'status' ? (videoSortAsc ? '(pending first)' : '(processed first)') : ''}`}
                      >
                        <div className="control-content">
                          <span className="control-label">Status</span>
                          <span className="control-subtext">sort by status</span>
                        </div>
                        <span className="sort-indicator">{videoSortBy === 'status' ? (videoSortAsc ? '↑' : '↓') : ''}</span>
                      </button>
                      <button
                        className={`control-button control-fixed ${showTileDetails ? 'active' : ''}`}
                        onClick={() => setShowTileDetails(!showTileDetails)}
                        title={showTileDetails ? 'Hide tile details' : 'Show tile details'}
                      >
                        <div className="control-content">
                          <span className="control-label">Details</span>
                          <span className="control-subtext">show/hide details</span>
                        </div>
                        <span className="sort-indicator"></span>
                      </button>
                    </div>

                    <div className="control-group zoom-group">
                      <button
                        className="control-button zoom-btn"
                        onClick={() => setTilesPerRow(Math.min(12, tilesPerRow + 1))}
                        disabled={tilesPerRow >= 12}
                        title="Zoom out"
                      >
                        <span className="control-label">−</span>
                      </button>
                      <span className="zoom-value">{getZoomPercentage(tilesPerRow)}%</span>
                      <button
                        className="control-button zoom-btn"
                        onClick={() => setTilesPerRow(Math.max(2, tilesPerRow - 1))}
                        disabled={tilesPerRow <= 2}
                        title="Zoom in"
                      >
                        <span className="control-label">+</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeDatasetStatus === 'loading' ? (
                <div className="loading-videos">
                  <RefreshCw size={24} className="index-loading-spinner" />
                  <span>{isMatchesView ? 'Loading workflow matches...' : isRunView ? 'Loading workflow output...' : 'Loading videos...'}</span>
                </div>
              ) : currentVideos.length > 0 ? (
                <div className="videos-container" ref={gridContainerRef}>
                  <PreviewViewportProvider>
                  <div
                    className={`videos-grid-compact ${showTileDetails ? 'show-details' : ''}`}
                    style={{
                      gridTemplateColumns: `repeat(${tilesPerRow}, minmax(0, 1fr))`,
                      columnGap: '4px',
                      rowGap: 0,
                      '--tiles-per-row': tilesPerRow
                    } as any}
                  >
                    {currentVideos.map((video) => {
                      const scopedRun = selectVideoRun(video.video_id, allSelectedIndexPromptRuns, selectedPromptRunId);
                      return (
                      <DeferredPreviewTile key={video.video_id}>
                        <PlaygroundFeatureAccessProvider enabled={selectedIndex.index_id === 'playground'}>
                        <VideoTile
                        video={video}
                        onTileClick={handleVideoTileClick}
                        onDelete={handleDeleteVideo}
                        isProcessing={video.status === 'processing'}
                        isReadOnly={selectedIndex.is_default || (!canMutateWorkspace && selectedIndex.index_id !== 'playground')}
                        showSegmentStatusBars
                        showSegmentStatusBarsAlways={showTileDetails}
                        runScopeId={scopedRun?.run_id || selectedPromptRunId}
                        runStatus={scopedRun?.status}
                        runLiveness={scopedRun?.liveness}
                        runFailureSummary={selectedPromptRunId ? selectedRunVideoFailureMap[video.video_id] || null : null}
                        indexId={selectedIndex.index_id}
                        onMarkerChange={handleVideoMarkerChange}
                          isActiveInSidebar={selectedVideoForDetail?.video_id === video.video_id}
                        />
                        </PlaygroundFeatureAccessProvider>
                      </DeferredPreviewTile>
                    ); })}
                  </div>
                  </PreviewViewportProvider>
                  
                  {/* Pagination Controls */}
                  {activeDatasetHasMore && (
                    <div className="pagination-controls">
                      <button
                        className="load-more-button"
                        onClick={isMatchesView
                          ? loadMoreWorkflowMatchVideos
                          : isRunView ? loadMoreRunVideos : loadMoreVideos}
                        disabled={activeDatasetIsLoadingMore || (!isRunView && isLoadingVideos)}
                      >
                        <ChevronDown size={16} />
                        {activeDatasetIsLoadingMore
                          ? 'Loading more videos...'
                          : isMatchesView
                            ? 'Load More Matches'
                            : isRunView
                              ? 'Load More Workflow Output'
                            : 'Load More Videos'}
                      </button>
                      <div className="pagination-info">
                        Showing {activeVideosSource.length} videos{activeDatasetHasMore ? ' (more available)' : ''}
                      </div>
                    </div>
                  )}
                </div>
              ) : filteredAndSortedVideos.length === 0 && activeVideosSource.length > 0 ? (
                <div className="index-no-results-state">
                  <Search size={48} />
                  <h3>No videos found</h3>
                  <p>No videos match your search criteria.</p>
                  <button
                    className="index-clear-search-button"
                    onClick={() => updateState({ videoSearchQuery: '' })}
                  >
                    Clear Search
                  </button>
                </div>
              ) : (
                <div className="index-empty-state">
                  <VideoIcon size={48} />
                  {activeDatasetError ? (
                    <>
                      <h3>{isMatchesView ? 'Workflow matches unavailable' : isRunView ? 'Workflow output unavailable' : 'Index videos unavailable'}</h3>
                      <p>{activeDatasetError}</p>
                    </>
                  ) : isRunView ? (
                    <>
                      <h3>{isMatchesView ? 'No matching media' : 'No media in this workflow execution'}</h3>
                      <p>{isMatchesView
                        ? 'No videos or segments matched the saved selection filter.'
                        : 'This workflow execution has no associated media.'}</p>
                    </>
                  ) : (
                    <>
                      <h3>No videos in this index</h3>
                      <p>Add your first video to get started with content processing.</p>
                      <span className="index-disabled-action" title={demoIndexDisabledReason}>
                      <FeatureAction feature={isDemoIndex || selectedIndex.index_id === 'playground' ? null : 'workspace_mutation'}><button
                        className="add-video-button"
                        onClick={() => selectedIndex.index_id === 'playground' ? navigate('/playground?step=media') : openAddVideoSidebar()}
                        disabled={isDemoIndex}
                        title={demoIndexDisabledReason}
                      >
                        <Upload size={16} />
                        Add Video
                      </button></FeatureAction>
                      </span>
                    </>
                  )}
                </div>
              )}
            </>
          ) : isRestoringSelectedIndex ? (
            <div className="loading-videos">
              <RefreshCw size={24} className="index-loading-spinner" />
              <span>Restoring index...</span>
            </div>
          ) : (
            <div className="index-empty-state">
              <Database size={48} />
              <h3>No indexes available</h3>
              <p>Create your first index to start organizing your videos.</p>
              <FeatureAction feature="workspace_mutation"><button
                className="index-create-button"
                onClick={() => {
                  historyModeRef.current = 'push';
                  updateState({ showCreateModal: true });
                }}
              >
                <Plus size={16} />
                Create Index
              </button></FeatureAction>
            </div>
          )}
        </div>
      </div>

      {/* Create Index Modal */}
      {showCreateModal && mutationLockedFeature && <LockedFeaturePanel feature={mutationLockedFeature} />}
      {showCreateModal && !mutationLockedFeature && (
        <div
          className="index-modal-overlay"
          onClick={() => {
            historyModeRef.current = 'push';
            updateState({ showCreateModal: false });
          }}
        >
          <div className="index-modal create-index-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Create New Index</h3>
            <form onSubmit={handleCreateIndex}>
              <div className="index-form-group">
                <label htmlFor="indexName">Index Name</label>
                <input
                  id="indexName"
                  type="text"
                  value={newIndexName}
                  onChange={(e) => updateState({ newIndexName: e.target.value })}
                  placeholder="Enter index name..."
                  className="index-form-input"
                  autoFocus
                />
              </div>


              <div className="index-modal-actions">
                <button
                  type="button"
                  className="index-secondary-button"
                  onClick={() => {
                    historyModeRef.current = 'push';
                    updateState({ 
                      showCreateModal: false,
                      newIndexName: ''
                    });
                  }}
                >
                  Cancel
                </button>
                <FeatureAction feature="workspace_mutation"><button
                  type="submit"
                  className="index-create-button"
                  disabled={!newIndexName.trim() || isLoading}
                >
                  Create
                </button></FeatureAction>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddVideoModal && mutationLockedFeature && <LockedFeaturePanel feature={mutationLockedFeature} />}
      <AddVideoSidebar
        isOpen={isAddVideoSidebarOpen}
        indexName={selectedIndex?.name || ''}
        selectedIndexId={selectedIndex?.index_id || null}
        mode={addVideoMode}
        onModeChange={handleAddVideoModeChange}
        onClose={closeAddVideoSidebar}
        connectors={importCapableConnectors}
        isLoadingConnectors={isLoadingConnectors}
        selectedConnectorId={selectedConnectorId}
        onSelectedConnectorIdChange={setSelectedConnectorId}
        sourcePrefix={importSourcePrefix}
        onSourcePrefixChange={setImportSourcePrefix}
        filePattern={importFilePattern}
        onFilePatternChange={setImportFilePattern}
        recursive={importRecursive}
        onRecursiveChange={setImportRecursive}
        importModeOverride={importModeOverride}
        onImportModeOverrideChange={setImportModeOverride}
        isCreatingImportJob={isCreatingImportJob}
        onImport={handleCloudImport}
        onLoadConnectors={loadConnectors}
        importLockedFeature={importLockedFeature}
        historyItems={ingestionHistory}
        historyLastSeenAt={ingestionHistoryLastSeenAt}
        isRefreshingHistory={isRefreshingIngestionHistory}
        onRefreshHistory={refreshIngestionHistory}
      />

      {error && !showExportModal && (
        <div className="index-error-message">
          <span>{error}</span>
          {visibleErrorAction && (
            <button className="error-action-button" onClick={() => navigate(visibleErrorAction.path)}>
              {visibleErrorAction.label}
            </button>
          )}
          <button onClick={() => {
            setError(null);
            setErrorAction(null);
          }}>×</button>
        </div>
      )}

      {Object.entries(pendingVideoDeletions).map(([videoId, deletion]) => (
        <div
          key={`deleting-video-${videoId}`}
          className="index-deletion-progress"
          role="status"
          aria-live="polite"
        >
          <RefreshCw size={14} className="index-spinning" />
          <span>
            Deleting {deletion.video_title} — {deletion.status}
          </span>
        </div>
      ))}

      {urlNotice && (
        <div className="index-error-message">
          {urlNotice}
          <button onClick={() => setUrlNotice(null)}>×</button>
        </div>
      )}

      {/* Video Detail Sidebar */}
      <PlaygroundFeatureAccessProvider enabled={selectedIndex?.index_id === 'playground'}>
      <VideoDetailModal
        video={selectedVideoForDetail}
        isOpen={selectedVideoForDetail !== null}
        onClose={handleCloseVideoDetail}
        indexId={selectedIndex?.index_id || undefined}
        playbackMode="continuous-video"
        runCatalog={allSelectedIndexPromptRuns}
        runScopeId={selectedVideoForDetail
          ? selectVideoRun(selectedVideoForDetail.video_id, allSelectedIndexPromptRuns, deepLinkRunId || selectedPromptRunId)?.run_id || null
          : null}
        deepLinkRunId={deepLinkRunId}
        onRunSelectionChange={(runId, options) => {
          historyModeRef.current = options?.userInitiated ? 'push' : 'replace';
          setDeepLinkRunId(runId);
          if (options?.userInitiated) {
            setResultLink(null);
            setDeepLinkTime(null);
            setDeepLinkFieldKey(null);
          }
        }}
        resultLink={resultLink}
        resultSelectionPending={isMatchesView && (!selectedMatchVideosState
          || (Boolean(selectedVideoForDetail)
            && !selectedMatchVideosState.matchesByVideoId[selectedVideoForDetail!.video_id]
            && (selectedMatchVideosState.isLoading || selectedMatchVideosState.hasMore)))}
        onResultLinkChange={(patch) => {
          historyModeRef.current = 'push';
          setResultLink((current) => ({
            videoId: selectedVideoForDetail!.video_id, runId: deepLinkRunId, indexId: selectedIndex?.index_id || null,
            segmentId: null, output: null, playback: null, metadata: null, resultScope: null, time: null, field: null,
            ...current, ...patch,
          }));
          if ('time' in patch) setDeepLinkTime(patch.time ?? null);
          if ('field' in patch) setDeepLinkFieldKey(patch.field ?? null);
        }}
        visibleSegmentIds={isMatchesView && selectedVideoForDetail
          ? selectedMatchVideosState?.matchesByVideoId[selectedVideoForDetail.video_id]?.matched_segment_ids
            || EMPTY_WORKFLOW_MATCH_SEGMENT_IDS
          : null}
        showVideoLevelOutput={!isMatchesView || Boolean(selectedVideoForDetail
          && selectedMatchVideosState?.matchesByVideoId[selectedVideoForDetail.video_id]?.video_matched)}
        deepLinkTime={deepLinkTime}
        deepLinkFieldKey={deepLinkFieldKey}
      />
      </PlaygroundFeatureAccessProvider>

      {/* Export Modal */}
      {showExportModal && exportLockedFeature && (
        <div className="index-modal-overlay" onClick={resetExportModalState}>
          <div className="index-modal index-export-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Export Index Metadata</h3>
            <LockedFeaturePanel feature={exportLockedFeature} />
            <div className="index-modal-actions">
              <button type="button" className="index-secondary-button" onClick={resetExportModalState}>
                Close
              </button>

            </div>
          </div>
        </div>
      )}

      {showExportModal && !exportLockedFeature && (
        <div
          className="index-modal-overlay"
          onClick={() => {
            if (!isExporting) resetExportModalState();
          }}
        >
          <div className="index-modal index-export-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Export Index Metadata</h3>
            <div className="index-export-options">
              <p>
                {isMatchesView
                  ? 'Exporting the selected matches from this workflow execution.'
                  : isRunView
                    ? 'Exporting all extracted results from this workflow execution.'
                    : 'Exporting the combined results currently selected for this index.'}
              </p>
              <fieldset className="index-export-destination">
                <legend className="index-export-section-title">Destination</legend>
                <div className="index-export-radio-group">
                  <label className="index-export-radio">
                    <input
                      type="radio"
                      name="export-destination"
                      checked={exportDestinationType === 'download'}
                      onChange={() => {
                        setExportDestinationType('download');
                        setExportSuccess(null);
                        setExportError(null);
                        setExportErrorAction(null);
                      }}
                    />
                    Download link
                  </label>
                  <label className="index-export-radio">
                    <input
                      type="radio"
                      name="export-destination"
                      checked={exportDestinationType === 'connector'}
                      onChange={() => {
                        setExportDestinationType('connector');
                        setExportSuccess(null);
                        setExportError(null);
                        setExportErrorAction(null);
                      }}
                    />
                    Cloud connector
                  </label>
                </div>

                {exportDestinationType === 'connector' && (
                  <div className="index-export-connector-fields">
                    {exportCapableConnectors.length === 0 ? (
                      <div className="index-export-empty-note">
                        No active export-capable connectors found.
                      </div>
                    ) : (
                      <>
                        <div className="index-form-group index-export-form-group">
                          <label htmlFor="index-export-connector">Connector</label>
                          <select
                            id="index-export-connector"
                            className="index-form-input index-export-select"
                            value={selectedExportConnectorId}
                            onChange={(e) => {
                              setSelectedExportConnectorId(e.target.value);
                              setExportSuccess(null);
                              setExportError(null);
                              setExportErrorAction(null);
                            }}
                          >
                            <option value="">Select a connector</option>
                            {exportCapableConnectors.map((connector) => (
                              <option key={connector.connector_id} value={connector.connector_id}>
                                {connector.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="index-form-group index-export-form-group">
                          <label htmlFor="index-export-subpath">
                            Destination subpath (optional)
                          </label>
                          <input
                            id="index-export-subpath"
                            className="index-form-input"
                            type="text"
                            value={exportDestinationSubpath}
                            onChange={(e) => {
                              setExportDestinationSubpath(e.target.value);
                              setExportSuccess(null);
                              setExportError(null);
                              setExportErrorAction(null);
                            }}
                            placeholder="e.g. team-a/reports"
                          />
                          <div className="index-export-field-hint">
                            File name is generated automatically.
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </fieldset>
              
              <div className="index-export-run-list" role="status">
                {isMatchesView && selectedPromptRun
                  ? `Scope: Matches from ${selectedPromptRun.prompt_name || selectedPromptRun.prompt_id || 'this workflow execution'}.`
                  : isRunView && selectedPromptRun
                    ? `Scope: All extracted results from ${selectedPromptRun.prompt_name || selectedPromptRun.prompt_id || 'this workflow execution'}.`
                    : 'Scope: Combined index results.'}
              </div>
            </div>
            
            {exportSuccess && (
              <div className="index-export-message success" role="status">
                <CheckCircle size={16} aria-hidden="true" />
                <span>{exportSuccess}</span>
              </div>
            )}

            {exportError && (
              <div className="index-export-message error" role="alert">
                <AlertCircle size={16} aria-hidden="true" />
                <span>{exportError}</span>
                {visibleExportErrorAction && (
                  <button
                    type="button"
                    className="index-export-error-action"
                    onClick={() => navigate(visibleExportErrorAction.path)}
                  >
                    {visibleExportErrorAction.label}
                  </button>
                )}
              </div>
            )}

            {exportHistoryError && (
              <div className="index-export-message error" role="status">
                <AlertCircle size={16} aria-hidden="true" />
                <span>{exportHistoryError}</span>
              </div>
            )}

            <section className="index-export-history" aria-labelledby="index-export-history-title">
              <h4 id="index-export-history-title" className="index-export-history-title">Previous Exports</h4>
              {isLoadingExports ? (
                <div className="index-export-history-loading" role="status">
                  <RefreshCw size={16} className="index-loading-spinner" aria-hidden="true" />
                  Loading export history...
                </div>
              ) : previousExports.length > 0 ? (
                <div className="index-export-history-list">
                  {previousExports.map((exp) => (
                    <div key={exp.export_id} className="index-export-history-item">
                      <div>
                        <div className="index-export-history-name">
                          {new Date(exp.created_at).toLocaleString()}
                        </div>
                        <div className="index-export-history-meta">
                          {exp.status === 'completed' && exp.file_size_bytes && (
                            <>Size: {(exp.file_size_bytes / 1024).toFixed(1)} KB</>
                          )}
                          {exp.status === 'completed' && getExportDeliveryAuthority(exp) === 'connector' && (
                            <div>
                              Delivered to: {exp.destination_uri || `${exp.destination_base_path || ''}${exp.destination_subpath ? `/${exp.destination_subpath}` : ''}`}
                            </div>
                          )}
                          {exp.status === 'processing' && 'Processing...'}
                          {exp.status === 'failed' && 'Failed'}
                        </div>
                      </div>
                      {getExportDeliveryAuthority(exp) === 'authenticated_download' && (
                        <button
                          type="button"
                          className="index-export-download-button"
                          onClick={() => handleDownloadExport(exp)}
                        >
                          <Download size={12} aria-hidden="true" />
                          Download
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="index-export-history-empty">
                  No previous exports for this index.
                </div>
              )}
            </section>
            
            <div className="index-modal-actions">
              <button
                type="button"
                className="index-secondary-button"
                onClick={resetExportModalState}
                disabled={isExporting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="index-primary-button"
                onClick={handleExportMetadata}
                disabled={
                  isExporting ||
                  (
                    exportDestinationType === 'connector' &&
                    (exportCapableConnectors.length === 0 || !selectedExportConnectorId)
                  )
                }
              >
                <Download size={16} />
                {isExporting
                  ? 'Exporting...'
                  : isMatchesView
                    ? 'Export Matches'
                    : isRunView
                      ? 'Export Run Results'
                      : 'Export Combined Index'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndexTab;
